import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { BrowserProvider, Contract } from "ethers";
import {
  hydrateDynamicTokenRecord,
  hydrateFullTokenRecord,
  normalizeTokenId,
  runWithConcurrency,
} from "../services/nftHydration";
import {
  getMulticallConfig,
  hydrateDynamicTokensViaMulticall,
  hydrateFullTokensViaMulticall,
  probeMulticall,
} from "../services/multicallAdapter";
import {
  buildEventUpdate,
  getEventIdentity,
  MARKETPLACE_EVENT_NAMES,
} from "../services/marketplaceEvents";
import { getIpfsGatewayBase, toIpfsGatewayUrl } from "../utils/ipfs";
import {
  INITIAL_MARKETPLACE_STATE,
  MARKETPLACE_CACHE_SCHEMA_VERSION,
  marketplaceReducer,
} from "../utils/nftStateReducer";
import {
  clearMarketplaceSummaryBuilder,
  endTimer,
  flushMarketplaceMetrics,
  getMarketplaceObservabilityConfig,
  incrementCounter,
  isMarketplaceObservabilityEnabled,
  recordEvent,
  recordMetric,
  setGauge,
  setMarketplaceSummaryBuilder,
  startTimer,
} from "../utils/marketplaceObservability";

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const DEV_MODE = process.env.NODE_ENV !== "production";
const MARKETPLACE_DEBUG = DEV_MODE && process.env.REACT_APP_MARKETPLACE_DEBUG === "1";
const MARKETPLACE_PERF_DEBUG = DEV_MODE && process.env.REACT_APP_MARKETPLACE_PERF_DEBUG === "1";
const MARKETPLACE_OBSERVABILITY_ENABLED = isMarketplaceObservabilityEnabled();

function isMarketplaceEventTraceEnabled() {
  if (MARKETPLACE_DEBUG) return true;
  if (process.env.REACT_APP_MARKETPLACE_EVENT_TRACE === "1") return true;
  if (typeof window === "undefined") return false;
  if (window.__MARKETPLACE_EVENT_TRACE__ === true) return true;
  try {
    return window.localStorage.getItem("marketplace:eventTrace") === "1";
  } catch {
    return false;
  }
}

function traceMarketplaceEvent(message, payload) {
  if (!isMarketplaceEventTraceEnabled()) return;
  if (payload === undefined) {
    console.log(`[marketplace-events] ${message}`);
    return;
  }
  console.log(`[marketplace-events] ${message}`, payload);
}

function classifyReplayError(error, fallbackCategory = "unknown") {
  const code = Number(error?.code ?? error?.error?.code);
  const message = String(error?.reason || error?.message || "").toLowerCase();

  if (code === 4001) return "user_rejected";
  if (code === -32005 || message.includes("rate") || message.includes("limit")) return "rate_limited";
  if (message.includes("timeout") || message.includes("network") || message.includes("failed to fetch")) {
    return "network";
  }
  if (message.includes("session")) return "session_inactive";
  if (message.includes("query") || message.includes("filter")) return "query_failed";
  return fallbackCategory;
}

function replayLogValue(value) {
  if (value == null) return "n/a";
  const n = Number(value);
  if (!Number.isFinite(n)) return "n/a";
  return String(Math.floor(n));
}

function logReplayAttempt(attempt) {
  if (!MARKETPLACE_OBSERVABILITY_ENABLED) return;
  console.log(
    `[marketplace-replay] latest=${replayLogValue(attempt.latestBlock)} cursorBefore=${replayLogValue(attempt.cursorBefore)} from=${replayLogValue(attempt.fromBlock)} to=${replayLogValue(attempt.toBlock)} cursorAfter=${replayLogValue(attempt.cursorAfter)} lag=${replayLogValue(attempt.lagBlocks)} events=${replayLogValue(attempt.eventsProcessed)} affectedTokens=${replayLogValue(attempt.affectedTokens)} duration=${replayLogValue(attempt.durationMs)} status=${attempt.status}`
  );
}

function toOptionalBlockNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.floor(numeric);
}

function cacheJsonReplacer(key, value) {
  if (typeof value === "bigint") return value.toString();
  return value;
}

function clampToPositiveInt(value, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.floor(parsed);
}

function safeParseJson(raw, fallback) {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function normalizeAddress(value) {
  return value ? String(value).toLowerCase() : null;
}

function stringifyArg(value) {
  if (typeof value === "bigint") return value.toString();
  if (Array.isArray(value)) return value.map(stringifyArg);
  if (value && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (!Number.isNaN(Number(k))) continue;
      out[k] = stringifyArg(v);
    }
    return out;
  }
  return value;
}

function getConfiguredStartBlock() {
  const configured = Number(
    process.env.REACT_APP_MARKETPLACE_START_BLOCK ||
      process.env.REACT_APP_MARKETPLACE_HISTORY_START_BLOCK ||
      process.env.REACT_APP_NFT_START_BLOCK ||
      process.env.SAVED_BLOCK ||
      0
  );
  return Number.isFinite(configured) && configured >= 0 ? configured : 0;
}

function extractTokenIdFromEvent(eventName, args) {
  if (eventName === "Transfer") {
    return normalizeTokenId(args?.tokenId ?? args?.[2]);
  }
  return normalizeTokenId(args?.tokenId ?? args?.[0]);
}

function getEventLogIndex(event) {
  const raw = event?.logIndex ?? event?.index ?? null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function buildIndexedEventRecord(eventName, args, event, blockTimestamp) {
  const eventKey = getEventIdentity(event) || `${eventName}:${event?.blockNumber || 0}:${getEventLogIndex(event)}`;
  return {
    eventKey,
    eventName,
    blockNumber: Number(event?.blockNumber || 0),
    logIndex: getEventLogIndex(event),
    transactionHash: event?.transactionHash || "",
    timestamp: blockTimestamp || null,
    args: stringifyArg(args || {}),
  };
}

function eventDisplayLabel(eventName) {
  switch (eventName) {
    case "Transfer":
      return "Minted/Transferred";
    case "CarbonCreditRetired":
      return "Retired";
    case "TokenListed":
      return "Listed";
    case "ListingCancelled":
      return "Listing Cancelled";
    case "TokenPurchased":
      return "Purchased";
    case "AuctionStarted":
      return "Auction Started";
    case "NewHighestBid":
      return "Bid Placed";
    case "AuctionEnded":
      return "Auction Ended";
    default:
      return eventName;
  }
}

function toEthDisplay(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "";
  return `${(n / 1e18).toFixed(4)} ETH`;
}

function stripSensitiveUrl(url) {
  if (!url) return "";
  try {
    const parsed = new URL(String(url));
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    const text = String(url);
    const q = text.indexOf("?");
    const h = text.indexOf("#");
    const cut = [q, h].filter((idx) => idx >= 0).sort((a, b) => a - b)[0];
    return cut == null ? text : text.slice(0, cut);
  }
}

function classifyMetadataFailure(error) {
  const message = String(error?.message || "").toLowerCase();
  if (message.includes("timeout") || message.includes("aborted")) return "timeout";
  if (message.includes("network") || message.includes("failed to fetch") || message.includes("cors")) return "cors_or_network_error";
  return "other";
}

function normalizeDirtyReason(reason) {
  const raw = String(reason || "event").toLowerCase();
  if (raw.includes("listed")) return "listed";
  if (raw.includes("cancel")) return "cancelled";
  if (raw.includes("purchased")) return "purchased";
  if (raw.includes("auction-start")) return "auction_started";
  if (raw.includes("highest-bid") || raw.includes("bid")) return "bid";
  if (raw.includes("auction-end")) return "auction_ended";
  if (raw.includes("retired")) return "retired";
  if (raw.includes("mint")) return "mint";
  if (raw.includes("transfer")) return "transfer";
  return raw;
}

function dirtyReasonPriority(reason) {
  switch (normalizeDirtyReason(reason)) {
    case "purchased":
    case "auction_ended":
      return 100;
    case "cancelled":
      return 90;
    case "listed":
      return 80;
    case "auction_started":
      return 70;
    case "bid":
      return 60;
    case "retired":
      return 50;
    case "mint":
      return 40;
    case "transfer":
      return 10;
    default:
      return 20;
  }
}

function chooseHydrationMode(reports) {
  const list = Array.isArray(reports) ? reports.filter(Boolean) : [];
  if (!list.length) return "bounded_concurrency";
  if (list.some((item) => item.mode === "fallback")) return "fallback";
  if (list.some((item) => item.mode === "multicall")) return "multicall";
  return "bounded_concurrency";
}

function formatHistoryEntry(eventItem) {
  const args = eventItem?.args || {};
  const eventName = eventItem?.eventName || "Unknown";

  const pickArg = (...keys) => {
    for (const key of keys) {
      const value = args?.[key];
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
    return null;
  };

  let by = "-";
  let extraInfo = "";

  if (eventName === "Transfer") {
    const from = pickArg("from", 0);
    const to = pickArg("to", 1);
    by = to || from || "-";
    if (from && to) {
      extraInfo = `${from} -> ${to}`;
    }
  } else if (eventName === "CarbonCreditRetired") {
    by = pickArg("retiredBy", "verifier", 1) || "-";
  } else if (eventName === "TokenListed") {
    by = pickArg("seller", 1) || "-";
    const price = pickArg("price", 2);
    if (price != null) extraInfo = `for ${toEthDisplay(price)}`;
  } else if (eventName === "ListingCancelled") {
    by = pickArg("seller", 1) || "-";
  } else if (eventName === "TokenPurchased") {
    by = pickArg("buyer", 1) || "-";
    const price = pickArg("price", 2);
    if (price != null) extraInfo = `bought for ${toEthDisplay(price)}`;
  } else if (eventName === "AuctionStarted") {
    by = pickArg("seller", 1) || "-";
    const p = pickArg("minPrice", "startingPrice", 2);
    if (p != null) extraInfo = `starting at ${toEthDisplay(p)}`;
  } else if (eventName === "NewHighestBid") {
    by = pickArg("bidder", 1) || "-";
    const amount = pickArg("amount", 2);
    if (amount != null) extraInfo = toEthDisplay(amount);
  } else if (eventName === "AuctionEnded") {
    by = pickArg("winner", 1) || "-";
    const amount = pickArg("amount", 2);
    if (amount != null) {
      extraInfo = `won with ${toEthDisplay(amount)}`;
    } else {
      extraInfo = "no bids";
    }
  }

  const date = eventItem?.timestamp
    ? new Date(Number(eventItem.timestamp) * 1000).toISOString()
    : `Block #${eventItem?.blockNumber || "-"}`;

  return {
    event: eventDisplayLabel(eventName),
    by,
    date,
    timestamp: Number(eventItem?.timestamp || 0),
    blockNumber: eventItem?.blockNumber,
    transactionHash: eventItem?.transactionHash,
    extraInfo,
  };
}

export function useMarketplaceNFTs({
  nftAddress,
  marketplaceAddress,
  nftAbi,
  marketplaceAbi,
  activeTab,
  sortBy,
  sortOrder,
  currentPage,
  itemsPerPage,
}) {
  const [state, dispatch] = useReducer(marketplaceReducer, INITIAL_MARKETPLACE_STATE);
  const [chainId, setChainId] = useState(null);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [now, setNow] = useState(Math.floor(Date.now() / 1000));
  const [reinitNonce, setReinitNonce] = useState(0);

  const contractsRef = useRef({ provider: null, nft: null, marketplace: null });
  const stateRef = useRef(state);
  const cacheKeyRef = useRef(null);
  const dirtyTokenMapRef = useRef(new Map());
  const dirtyFlushTimerRef = useRef(null);
  const replayTimerRef = useRef(null);
  const persistTimerRef = useRef(null);
  const lastVisibleHydrationKeyRef = useRef("");
  const seenEventsRef = useRef(new Set());
  const eventCursorRef = useRef(null);
  const unmountedRef = useRef(false);
  const sessionIdRef = useRef(0);
  const retryStateRef = useRef(new Map());
  const retryHydrateRef = useRef(null);
  const queueDirtyTokenRef = useRef(null);
  const abortControllersRef = useRef(new Map());
  const inFlightHydrationsRef = useRef(new Map());
  const backgroundHydrationRef = useRef({ inFlight: false, key: "" });
  const bootstrappingOwnerRef = useRef(null);
  const recomputeOwnershipForAccountRef = useRef(null);
  const reconcileCursorRef = useRef(0);
  const periodicReconcileRef = useRef({ inFlight: false });
  const multicallProbeRef = useRef({ key: null, usable: null });
  const firstVisibleHydrationMsRef = useRef(null);
  const observabilitySummaryRef = useRef({
    cacheRestore: null,
    visibleHydration: null,
    backgroundHydration: null,
    replay: null,
    dirtyFlush: {
      count: 0,
      avgMs: 0,
      lastMs: 0,
      lastTokens: 0,
      lastFailedTokens: 0,
      lastFallbackTokens: 0,
      lastReason: null,
    },
    metadata: {
      fetchCount: 0,
      cacheHits: 0,
      failures: 0,
      fallbackUsed: 0,
      lastDurationMs: 0,
    },
    multicall: {
      enabled: false,
      address: null,
      chainId: null,
      aggregateCalls: 0,
      lowLevelCalls: 0,
      partialFailures: 0,
      fallbackTokens: 0,
      durationMs: 0,
      disabledReason: null,
      badDataCount: 0,
      probeSuccess: 0,
      probeFailure: 0,
    },
  });

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (!MARKETPLACE_OBSERVABILITY_ENABLED) return undefined;

    const cfg = getMarketplaceObservabilityConfig();
    recordEvent("marketplace.observability.enabled", {
      sampleRate: cfg.sampleRate,
      summaryIntervalMs: cfg.summaryIntervalMs,
      console: cfg.console,
    });

    setMarketplaceSummaryBuilder((snapshot) => {
      const summary = observabilitySummaryRef.current;
      const metadataFailures = snapshot?.counters?.["marketplace.metadata.fetch.failure"]?.value ?? summary.metadata.failures;
      const dirtyCount = snapshot?.counters?.["marketplace.dirty.flush.count"]?.value ?? summary.dirtyFlush.count;

      return {
        lines: [
          `cacheRestore: ${Math.round(summary.cacheRestore?.durationMs || 0)}ms, tokens=${summary.cacheRestore?.tokensRestored || 0}, events=${summary.cacheRestore?.eventsRestored || 0}`,
          `visibleHydration: ${Math.round(summary.visibleHydration?.durationMs || 0)}ms, mode=${summary.visibleHydration?.mode || "n/a"}, tokens=${summary.visibleHydration?.tokens || 0}, fallback=${summary.visibleHydration?.fallbackTokens || 0}`,
          `backgroundHydration: ${Math.round(summary.backgroundHydration?.durationMs || 0)}ms, mode=${summary.backgroundHydration?.mode || "n/a"}, tokens=${summary.backgroundHydration?.tokens || 0}, fallback=${summary.backgroundHydration?.fallbackTokens || 0}`,
          `metadataFailures: ${metadataFailures}`,
          `multicall: enabled=${summary.multicall.enabled}, aggregateCalls=${summary.multicall.aggregateCalls || 0}, partialFailures=${summary.multicall.partialFailures || 0}, fallbackTokens=${summary.multicall.fallbackTokens || 0}`,
          `replayLag: ${summary.replay?.lagBlocks ?? 0} blocks, replayDuration=${Math.round(summary.replay?.durationMs || 0)}ms`,
          `dirtyFlushes: ${dirtyCount}, avg=${Math.round(summary.dirtyFlush?.avgMs || 0)}ms`,
        ],
      };
    });

    return () => {
      flushMarketplaceMetrics({ force: true });
      clearMarketplaceSummaryBuilder();
    };
  }, []);

  const cacheKey = useMemo(() => {
    if (!chainId || !nftAddress || !marketplaceAddress) return null;
    return `marketplace:${chainId}:${nftAddress.toLowerCase()}:${marketplaceAddress.toLowerCase()}`;
  }, [chainId, nftAddress, marketplaceAddress]);

  const tokensArray = useMemo(() => {
    return state.tokenOrder
      .map((id) => state.tokensById[id])
      .filter((token) => {
        if (!token) return false;
        if (token.hiddenFromMarketplace) return false;
        if (token.metadataStatus === "missing-uri") return false;
        return true;
      });
  }, [state.tokenOrder, state.tokensById]);

  const filteredTokenIds = useMemo(() => {
    const accountLower = normalizeAddress(currentAccount);

    const filtered = tokensArray.filter((token) => {
      const retired = Boolean(token.retired);
      const listed = Boolean(token.isListed);
      const auction = Boolean(token.isAuction || token.auctionData?.active);

      if (activeTab === "retired") return retired;
      if (activeTab === "listed") return listed && !auction;
      if (activeTab === "auction") return auction;
      return true;
    });

    filtered.sort((a, b) => {
      const dir = sortOrder === "asc" ? 1 : -1;

      if (sortBy === "price") {
        const ap = Number(a.price || 0);
        const bp = Number(b.price || 0);
        return (ap - bp) * dir;
      }

      if (sortBy === "name") {
        const an = String(a.name || "").toLowerCase();
        const bn = String(b.name || "").toLowerCase();
        if (an < bn) return -1 * dir;
        if (an > bn) return 1 * dir;
      }

      const aid = Number(a.tokenId || 0);
      const bid = Number(b.tokenId || 0);
      return (aid - bid) * dir;
    });

    return filtered.map((token) => token.tokenId).filter(Boolean).map(String).map((id) => {
      const token = state.tokensById[id];
      return {
        id,
        isOwner: Boolean(accountLower && normalizeAddress(token?.owner) === accountLower),
      };
    }).map((entry) => entry.id);
  }, [activeTab, currentAccount, sortBy, sortOrder, state.tokensById, tokensArray]);

  const paginatedTokenIds = useMemo(() => {
    const start = (Math.max(currentPage, 1) - 1) * itemsPerPage;
    return filteredTokenIds.slice(start, start + itemsPerPage);
  }, [filteredTokenIds, currentPage, itemsPerPage]);

  const paginatedNfts = useMemo(() => {
    return paginatedTokenIds
      .map((id) => state.tokensById[id])
      .filter(Boolean)
      .map((token) => {
        const accountLower = normalizeAddress(currentAccount);
        const ownerLower = normalizeAddress(token.owner);
        return {
          ...token,
          isOwner: Boolean(accountLower && ownerLower && accountLower === ownerLower),
        };
      });
  }, [paginatedTokenIds, state.tokensById, currentAccount]);

  const listedCount = useMemo(
    () => tokensArray.filter((token) => token.isListed && !(token.isAuction || token.auctionData?.active)).length,
    [tokensArray]
  );

  const auctionCount = useMemo(
    () => tokensArray.filter((token) => token.isAuction || token.auctionData?.active).length,
    [tokensArray]
  );

  const retiredCount = useMemo(
    () => tokensArray.filter((token) => token.retired).length,
    [tokensArray]
  );

  const activeAuctionTokenIds = useMemo(
    () => tokensArray.filter((token) => token.isAuction || token.auctionData?.active).map((token) => String(token.tokenId)),
    [tokensArray]
  );

  const setEventCursor = useCallback((blockNumber) => {
    const n = Number(blockNumber);
    if (!Number.isFinite(n)) return;
    if (eventCursorRef.current != null && n <= Number(eventCursorRef.current)) return;
    eventCursorRef.current = n;
    dispatch({ type: "SET_EVENT_CURSOR", payload: { lastProcessedEventBlock: n } });
  }, []);

  const getBlockTimestampCached = useCallback(async (blockNumber) => {
    const key = String(blockNumber);
    const existing = stateRef.current.blockTimestampByNumber?.[key];
    if (existing) return Number(existing);

    const provider = contractsRef.current.provider;
    if (!provider) return null;

    const block = await provider.getBlock(blockNumber).catch(() => null);
    const timestamp = block?.timestamp || null;
    if (timestamp) {
      dispatch({ type: "SET_BLOCK_TIMESTAMP", payload: { blockNumber, timestamp } });
    }
    return timestamp;
  }, []);

  const cancelAllInFlightWork = useCallback(() => {
    for (const timer of retryStateRef.current.values()) {
      if (timer?.timeoutId) {
        window.clearTimeout(timer.timeoutId);
      }
    }
    retryStateRef.current.clear();

    for (const controller of abortControllersRef.current.values()) {
      controller.abort();
    }
    abortControllersRef.current.clear();

    inFlightHydrationsRef.current.clear();
    dirtyTokenMapRef.current.clear();

    if (dirtyFlushTimerRef.current) {
      window.clearTimeout(dirtyFlushTimerRef.current);
      dirtyFlushTimerRef.current = null;
    }
  }, []);

  const isSessionActive = useCallback((sessionId) => {
    return !unmountedRef.current && sessionId === sessionIdRef.current;
  }, []);

  const clearRetryState = useCallback((tokenId) => {
    const normalizedId = normalizeTokenId(tokenId);
    for (const mode of ["dynamic", "full"]) {
      const key = `${mode}:${normalizedId}`;
      const entry = retryStateRef.current.get(key);
      if (entry?.timeoutId) {
        window.clearTimeout(entry.timeoutId);
      }
      retryStateRef.current.delete(key);
    }
  }, []);

  const scheduleRetry = useCallback(
    (tokenId, mode, reason, error) => {
      const normalizedId = normalizeTokenId(tokenId);
      if (!normalizedId) return;

      const key = `${mode}:${normalizedId}`;
      const existing = retryStateRef.current.get(key);
      if (existing?.timeoutId) return;

      const prevAttempt = existing?.attempt || 0;
      const delays = [2000, 5000, 15000, 30000, 60000];
      const code = Number(error?.code ?? error?.error?.code);
      const message = String(error?.message || "").toLowerCase();
      const walletBusy = code === -32002 || message.includes("-32002") || message.includes("already pending");
      const rateLimited = message.includes("rate") && message.includes("limit");

      let delay = delays[Math.min(prevAttempt, delays.length - 1)];
      if (walletBusy) delay = Math.max(delay, 30000);
      if (rateLimited) delay = Math.max(delay, 15000);

      const timeoutId = window.setTimeout(async () => {
        retryStateRef.current.delete(key);

        try {
          if (retryHydrateRef.current) {
            await retryHydrateRef.current(normalizedId, mode, reason);
          }
        } catch {
          // hydrate functions already reschedule on failure
        }
      }, delay);

      retryStateRef.current.set(key, {
        attempt: prevAttempt + 1,
        timeoutId,
      });

      if (MARKETPLACE_DEBUG) {
        console.log(`[marketplace] scheduled ${mode} retry token=${normalizedId} in ${delay}ms (${reason})`);
      }
    },
    []
  );

  const queryTokenEventsBounded = useCallback(async (tokenId, fromBlock, toBlock) => {
    const { nft, marketplace } = contractsRef.current;
    if (!nft || !marketplace) return [];
    if (!Number.isFinite(fromBlock) || !Number.isFinite(toBlock) || fromBlock > toBlock) return [];

    const normalized = normalizeTokenId(tokenId);
    if (!normalized) return [];

    const [
      transferEvents,
      retiredEvents,
      listedEvents,
      cancelledEvents,
      purchasedEvents,
      auctionStartedEvents,
      bidEvents,
      auctionEndedEvents,
    ] = await Promise.all([
      nft.queryFilter(nft.filters.Transfer(null, null, normalized), fromBlock, toBlock).catch(() => []),
      nft.queryFilter(nft.filters.CarbonCreditRetired(normalized), fromBlock, toBlock).catch(() => []),
      marketplace.queryFilter(marketplace.filters.TokenListed(normalized), fromBlock, toBlock).catch(() => []),
      marketplace.queryFilter(marketplace.filters.ListingCancelled(normalized), fromBlock, toBlock).catch(() => []),
      marketplace.queryFilter(marketplace.filters.TokenPurchased(normalized), fromBlock, toBlock).catch(() => []),
      marketplace.queryFilter(marketplace.filters.AuctionStarted(normalized), fromBlock, toBlock).catch(() => []),
      marketplace.queryFilter(marketplace.filters.NewHighestBid(normalized), fromBlock, toBlock).catch(() => []),
      marketplace.queryFilter(marketplace.filters.AuctionEnded(normalized), fromBlock, toBlock).catch(() => []),
    ]);

    const combined = [];
    for (const event of transferEvents) combined.push({ name: "Transfer", event });
    for (const event of retiredEvents) combined.push({ name: "CarbonCreditRetired", event });
    for (const event of listedEvents) combined.push({ name: "TokenListed", event });
    for (const event of cancelledEvents) combined.push({ name: "ListingCancelled", event });
    for (const event of purchasedEvents) combined.push({ name: "TokenPurchased", event });
    for (const event of auctionStartedEvents) combined.push({ name: "AuctionStarted", event });
    for (const event of bidEvents) combined.push({ name: "NewHighestBid", event });
    for (const event of auctionEndedEvents) combined.push({ name: "AuctionEnded", event });

    combined.sort((a, b) => {
      const blockDelta = Number(a.event?.blockNumber || 0) - Number(b.event?.blockNumber || 0);
      if (blockDelta !== 0) return blockDelta;
      return getEventLogIndex(a.event) - getEventLogIndex(b.event);
    });

    return combined;
  }, []);

  const indexEvent = useCallback(
    async (eventName, args, event, source = "live", options = { applyPatch: true, queueHydration: true }) => {
      const eventIdentity = getEventIdentity(event);
      if (eventIdentity) {
        if (seenEventsRef.current.has(eventIdentity)) {
          traceMarketplaceEvent("duplicate skipped", {
            source,
            eventName,
            eventIdentity,
            blockNumber: Number(event?.blockNumber || 0),
            logIndex: getEventLogIndex(event),
          });
          return;
        }
        seenEventsRef.current.add(eventIdentity);
        if (seenEventsRef.current.size > 6000) {
          const first = seenEventsRef.current.values().next().value;
          if (first) seenEventsRef.current.delete(first);
        }
      }

      const tokenId = extractTokenIdFromEvent(eventName, args);
      if (!tokenId) {
        traceMarketplaceEvent("event without tokenId ignored", { source, eventName, args });
        return;
      }

      const blockNumber = Number(event?.blockNumber || 0);
      const timestamp = blockNumber > 0 ? await getBlockTimestampCached(blockNumber) : null;

      const indexedEvent = buildIndexedEventRecord(eventName, args, event, timestamp);
      dispatch({ type: "INDEX_EVENT", payload: { tokenId, event: indexedEvent } });
      traceMarketplaceEvent("indexed", {
        source,
        eventName,
        tokenId,
        blockNumber,
        logIndex: getEventLogIndex(event),
        applyPatch: Boolean(options.applyPatch),
      });

      if (options.applyPatch) {
        const update = buildEventUpdate({ eventName, args, event });
        if (update) {
          dispatch({ type: "MERGE_TOKEN", payload: { tokenId: update.tokenId, patch: update.patch } });
          traceMarketplaceEvent("patch merged", {
            source,
            eventName,
            tokenId: update.tokenId,
            hydration: update.hydration,
            reason: update.reason,
          });
          if (options.queueHydration) {
            if (queueDirtyTokenRef.current) {
              queueDirtyTokenRef.current(update.tokenId, update.hydration, update.reason);
            }
          }
        } else {
          traceMarketplaceEvent("no patch produced", { source, eventName, tokenId });
        }
      }

      if (Number.isFinite(blockNumber) && blockNumber > 0) {
        setEventCursor(blockNumber);
        traceMarketplaceEvent("cursor advanced by event", {
          source,
          eventName,
          tokenId,
          blockNumber,
        });
      }
    },
    [getBlockTimestampCached, setEventCursor]
  );

  const recoverAuctionStartBlock = useCallback(async (tokenId) => {
    const normalizedId = normalizeTokenId(tokenId);
    if (!normalizedId) return null;

    const indexed = (stateRef.current.eventsByTokenId?.[normalizedId] || [])
      .filter((entry) => entry.eventName === "AuctionStarted")
      .sort((a, b) => Number(b.blockNumber || 0) - Number(a.blockNumber || 0));

    if (indexed.length > 0) {
      return Number(indexed[0].blockNumber || 0) || null;
    }

    const { provider, marketplace } = contractsRef.current;
    if (!provider || !marketplace) return null;

    const latest = Number(await provider.getBlockNumber().catch(() => NaN));
    if (!Number.isFinite(latest)) return null;

    const configuredStart = getConfiguredStartBlock();
    const lookback = clampToPositiveInt(process.env.REACT_APP_MARKETPLACE_AUCTION_RECOVERY_LOOKBACK, 250000);
    const fromBlock = Math.max(configuredStart, latest - lookback);

    const events = await marketplace
      .queryFilter(marketplace.filters.AuctionStarted(normalizedId), fromBlock, latest)
      .catch(() => []);

    if (!events.length) return null;

    events.sort((a, b) => {
      const blockDelta = Number(a.blockNumber || 0) - Number(b.blockNumber || 0);
      if (blockDelta !== 0) return blockDelta;
      return Number(a.logIndex || 0) - Number(b.logIndex || 0);
    });

    const latestEvent = events[events.length - 1];
    const block = Number(latestEvent?.blockNumber || 0) || null;

    if (latestEvent) {
      await indexEvent("AuctionStarted", latestEvent.args, latestEvent, "auction-recovery", {
        applyPatch: false,
        queueHydration: false,
      });
    }

    if (block) {
      dispatch({
        type: "MERGE_TOKEN",
        payload: {
          tokenId: normalizedId,
          patch: {
            auctionData: {
              auctionStartBlock: block,
            },
          },
        },
      });
    }

    return block;
  }, [indexEvent]);

  const hydrateDynamicToken = useCallback(
    async (tokenId, reason = "dynamic") => {
      const normalizedId = normalizeTokenId(tokenId);
      if (!normalizedId) return;
      const { nft, marketplace } = contractsRef.current;
      if (!nft || !marketplace) return;

      const sessionId = sessionIdRef.current;
      const key = `dynamic:${normalizedId}`;

      if (inFlightHydrationsRef.current.has(key)) {
        return inFlightHydrationsRef.current.get(key);
      }

      const task = (async () => {
        try {
          const previous = stateRef.current.tokensById[normalizedId] || {};
          let auctionStartBlock = previous?.auctionData?.auctionStartBlock ?? null;

          const patch = await hydrateDynamicTokenRecord({
            tokenId: normalizedId,
            nftContract: nft,
            marketplaceContract: marketplace,
            account: currentAccount,
            existingAuctionStartBlock: auctionStartBlock,
          });

          if (!isSessionActive(sessionId)) return;

          if (patch?.auctionData?.active && !patch?.auctionData?.auctionStartBlock) {
            const recovered = await recoverAuctionStartBlock(normalizedId);
            if (recovered) {
              patch.auctionData = {
                ...patch.auctionData,
                auctionStartBlock: recovered,
              };
            }
          }

          if (!isSessionActive(sessionId)) return;

          if (MARKETPLACE_OBSERVABILITY_ENABLED && (patch?.metadataStatus === "fallback" || patch?.metadataStatus === "missing-uri")) {
            incrementCounter("marketplace.metadata.fallback.used", {
              status: patch?.metadataStatus || "fallback",
              reason,
            });
            observabilitySummaryRef.current.metadata.fallbackUsed += 1;
          }

          dispatch({ type: "UPSERT_TOKEN", payload: patch });
          dispatch({ type: "SET_TOKEN_ERROR", payload: { tokenId: normalizedId, error: null } });
          clearRetryState(normalizedId);
        } catch (error) {
          if (!isSessionActive(sessionId)) return;
          dispatch({ type: "SET_TOKEN_ERROR", payload: { tokenId: normalizedId, error: error?.message || "Dynamic hydration failed" } });
          scheduleRetry(normalizedId, "dynamic", reason, error);
          throw error;
        } finally {
          inFlightHydrationsRef.current.delete(key);
        }
      })();

      inFlightHydrationsRef.current.set(key, task);
      return task;
    },
    [clearRetryState, currentAccount, isSessionActive, recoverAuctionStartBlock, scheduleRetry]
  );

  const hydrateFullToken = useCallback(
    async (tokenId, reason = "full") => {
      const normalizedId = normalizeTokenId(tokenId);
      if (!normalizedId) return;

      const { nft, marketplace } = contractsRef.current;
      if (!nft || !marketplace) return;

      const sessionId = sessionIdRef.current;
      const key = `full:${normalizedId}`;
      if (inFlightHydrationsRef.current.has(key)) {
        return inFlightHydrationsRef.current.get(key);
      }

      const task = (async () => {
        const previousToken = stateRef.current.tokensById[normalizedId] || {};
        const controller = new AbortController();
        abortControllersRef.current.set(key, controller);

        try {
          const patch = await hydrateFullTokenRecord({
            tokenId: normalizedId,
            nftContract: nft,
            marketplaceContract: marketplace,
            account: currentAccount,
            metadataByUri: stateRef.current.metadataByUri,
            setMetadataByUri: (uri, metadata) => {
              dispatch({ type: "SET_METADATA", payload: { uri, metadata } });
            },
            previousToken,
            signal: controller.signal,
          });

          if (!isSessionActive(sessionId)) return;

          if (patch?.auctionData?.active && !patch?.auctionData?.auctionStartBlock) {
            const recovered = await recoverAuctionStartBlock(normalizedId);
            if (recovered) {
              patch.auctionData = {
                ...patch.auctionData,
                auctionStartBlock: recovered,
              };
            }
          }

          if (!isSessionActive(sessionId)) return;

          dispatch({ type: "UPSERT_TOKEN", payload: patch });
          dispatch({ type: "SET_TOKEN_ERROR", payload: { tokenId: normalizedId, error: null } });
          clearRetryState(normalizedId);
        } catch (error) {
          if (controller.signal.aborted || !isSessionActive(sessionId)) return;
          dispatch({ type: "SET_TOKEN_ERROR", payload: { tokenId: normalizedId, error: error?.message || "Full hydration failed" } });
          scheduleRetry(normalizedId, "full", reason, error);
          throw error;
        } finally {
          abortControllersRef.current.delete(key);
          inFlightHydrationsRef.current.delete(key);
        }
      })();

      inFlightHydrationsRef.current.set(key, task);
      return task;
    },
    [clearRetryState, currentAccount, isSessionActive, recoverAuctionStartBlock, scheduleRetry]
  );

  useEffect(() => {
    retryHydrateRef.current = async (tokenId, mode, reason) => {
      if (mode === "full") {
        await hydrateFullToken(tokenId, `retry:${reason}`);
      } else {
        await hydrateDynamicToken(tokenId, `retry:${reason}`);
      }
    };
  }, [hydrateDynamicToken, hydrateFullToken]);

  const fetchMetadataForTokenUri = useCallback(async (tokenURI) => {
    if (!tokenURI) {
      if (MARKETPLACE_OBSERVABILITY_ENABLED) {
        incrementCounter("marketplace.metadata.fallback.used", { reason: "missing-token-uri" });
        observabilitySummaryRef.current.metadata.fallbackUsed += 1;
      }
      return {
        metadata: null,
        metadataStatus: "missing",
      };
    }

    if (MARKETPLACE_OBSERVABILITY_ENABLED) {
      incrementCounter("marketplace.metadata.fetch.count", { source: "gateway" });
      observabilitySummaryRef.current.metadata.fetchCount += 1;
    }

    const cached = stateRef.current.metadataByUri?.[tokenURI];
    if (cached) {
      if (MARKETPLACE_OBSERVABILITY_ENABLED) {
        incrementCounter("marketplace.metadata.cache.hit", { source: "memory" });
        observabilitySummaryRef.current.metadata.cacheHits += 1;
      }
      return {
        metadata: cached,
        metadataStatus: "cached",
      };
    }

    const url = toIpfsGatewayUrl(tokenURI, getIpfsGatewayBase());
    const timerId = MARKETPLACE_OBSERVABILITY_ENABLED
      ? startTimer("marketplace.metadata.fetch.duration_ms", { gateway: stripSensitiveUrl(url) })
      : null;

    try {
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`Metadata request failed (${res.status})`);
      }

      const metadata = await res.json();
      dispatch({ type: "SET_METADATA", payload: { uri: tokenURI, metadata } });

      if (MARKETPLACE_OBSERVABILITY_ENABLED) {
        const elapsed = endTimer(timerId, { status: "ok" });
        observabilitySummaryRef.current.metadata.lastDurationMs = Number(elapsed || 0);
      }

      return {
        metadata,
        metadataStatus: "loaded",
      };
    } catch (error) {
      if (MARKETPLACE_OBSERVABILITY_ENABLED) {
        endTimer(timerId, { status: "failure" });
        incrementCounter("marketplace.metadata.fetch.failure", {
          type: classifyMetadataFailure(error),
          gateway: stripSensitiveUrl(url),
        });

        const failureType = classifyMetadataFailure(error);
        if (failureType === "timeout") {
          incrementCounter("marketplace.metadata.timeout", { gateway: stripSensitiveUrl(url) });
        }
        if (failureType === "cors_or_network_error") {
          incrementCounter("marketplace.metadata.cors_or_network_error", { gateway: stripSensitiveUrl(url) });
        }

        observabilitySummaryRef.current.metadata.failures += 1;
      }
      throw error;
    }
  }, []);

  const hydrateTokenIds = useCallback(
    async (ids, hydration = "dynamic", reason = "bulk", concurrency = 6) => {
      if (!Array.isArray(ids) || ids.length === 0) {
        return {
          mode: "bounded_concurrency",
          tokenCount: 0,
          fallbackTokens: 0,
          failedTokens: 0,
          aggregateCalls: 0,
          lowLevelCalls: 0,
          partialFailures: 0,
        };
      }

      const normalizedIds = Array.from(new Set(ids.map(normalizeTokenId).filter(Boolean)));
      if (!normalizedIds.length) {
        return {
          mode: "bounded_concurrency",
          tokenCount: 0,
          fallbackTokens: 0,
          failedTokens: 0,
          aggregateCalls: 0,
          lowLevelCalls: 0,
          partialFailures: 0,
        };
      }

      const report = {
        mode: "bounded_concurrency",
        tokenCount: normalizedIds.length,
        fallbackTokens: 0,
        failedTokens: 0,
        aggregateCalls: 0,
        lowLevelCalls: 0,
        partialFailures: 0,
        multicallDisabledReason: null,
      };

      const { provider, nft, marketplace } = contractsRef.current;
      const sessionId = sessionIdRef.current;
      const canUseMulticall =
        (hydration === "dynamic" || hydration === "full") &&
        normalizedIds.length > 1 &&
        provider &&
        nft &&
        marketplace;

      if (canUseMulticall) {
        const multicallConfig = getMulticallConfig();
        report.multicallDisabledReason = multicallConfig.reason;

        if (MARKETPLACE_OBSERVABILITY_ENABLED) {
          observabilitySummaryRef.current.multicall.enabled = Boolean(multicallConfig.enabled);
          observabilitySummaryRef.current.multicall.address = multicallConfig.address || null;
          observabilitySummaryRef.current.multicall.chainId = Number(chainId || 0) || null;
        }

        if (multicallConfig.configured) {
          const chainRef = Number(chainId || 0);
          const probeKey = `${chainRef}:${multicallConfig.address.toLowerCase()}`;

          let probeUsable = null;
          if (multicallProbeRef.current.key === probeKey) {
            probeUsable = multicallProbeRef.current.usable;
          }

          if (probeUsable == null) {
            probeUsable = await probeMulticall(provider);
            multicallProbeRef.current = {
              key: probeKey,
              usable: probeUsable,
            };

            if (MARKETPLACE_OBSERVABILITY_ENABLED) {
              if (probeUsable) {
                incrementCounter("marketplace.multicall.probe.success", {
                  chainId: chainRef,
                  address: multicallConfig.address,
                });
                observabilitySummaryRef.current.multicall.probeSuccess += 1;
              } else {
                incrementCounter("marketplace.multicall.probe.failure", {
                  chainId: chainRef,
                  address: multicallConfig.address,
                });
                observabilitySummaryRef.current.multicall.probeFailure += 1;
              }
            }

            if (MARKETPLACE_DEBUG) {
              if (probeUsable) {
                console.log(`[multicall] probe success chain=${chainRef} address=${multicallConfig.address}`);
              } else {
                console.warn(`[multicall] probe failed chain=${chainRef} address=${multicallConfig.address}; using fallback`);
              }
            }
          }

          if (probeUsable) {
            const startedAt = performance.now();
            const timerId = MARKETPLACE_OBSERVABILITY_ENABLED
              ? startTimer("marketplace.multicall.duration_ms", { hydration, reason, chainId: chainRef })
              : null;

            try {
              const result = hydration === "full"
                ? await hydrateFullTokensViaMulticall({
                    provider,
                    nftContract: nft,
                    marketplaceContract: marketplace,
                    nftInterface: nft.interface,
                    marketplaceInterface: marketplace.interface,
                    tokenIds: normalizedIds,
                    userAccount: currentAccount,
                    currentState: stateRef.current.tokensById,
                    metadataCache: stateRef.current.metadataByUri,
                    fetchMetadata: fetchMetadataForTokenUri,
                  })
                : await hydrateDynamicTokensViaMulticall({
                    provider,
                    nftContract: nft,
                    marketplaceContract: marketplace,
                    nftInterface: nft.interface,
                    marketplaceInterface: marketplace.interface,
                    tokenIds: normalizedIds,
                    userAccount: currentAccount,
                    currentState: stateRef.current.tokensById,
                  });

              if (!isSessionActive(sessionId)) return;

              for (const patchRaw of result.patches || []) {
                let patch = patchRaw;
                const normalizedPatchId = normalizeTokenId(patch?.tokenId);
                if (!normalizedPatchId) continue;

                if (patch?.auctionData?.active && !patch?.auctionData?.auctionStartBlock) {
                  const recovered = await recoverAuctionStartBlock(normalizedPatchId);
                  if (recovered) {
                    patch = {
                      ...patch,
                      auctionData: {
                        ...patch.auctionData,
                        auctionStartBlock: recovered,
                      },
                    };
                  }
                }

                if (!isSessionActive(sessionId)) return;

                if (MARKETPLACE_OBSERVABILITY_ENABLED && (patch?.metadataStatus === "fallback" || patch?.metadataStatus === "missing-uri")) {
                  incrementCounter("marketplace.metadata.fallback.used", {
                    status: patch?.metadataStatus || "fallback",
                    reason,
                    hydration,
                  });
                  observabilitySummaryRef.current.metadata.fallbackUsed += 1;
                }

                dispatch({ type: "UPSERT_TOKEN", payload: patch });
                dispatch({ type: "SET_TOKEN_ERROR", payload: { tokenId: normalizedPatchId, error: null } });
                clearRetryState(normalizedPatchId);
              }

              const failedIds = Array.from(new Set((result.failedTokenIds || []).map(normalizeTokenId).filter(Boolean)));

              report.aggregateCalls = Number(result?.stats?.aggregateCalls || 0);
              report.lowLevelCalls = Number(result?.stats?.lowLevelCalls || 0);
              report.partialFailures = Number(result?.stats?.partialFailureCount || 0);
              report.fallbackTokens = failedIds.length;
              report.mode = failedIds.length > 0 ? "fallback" : "multicall";

              if (MARKETPLACE_OBSERVABILITY_ENABLED) {
                const elapsedTimer = endTimer(timerId, {
                  hydration,
                  reason,
                  mode: report.mode,
                });
                const elapsed = Math.round(performance.now() - startedAt);

                recordMetric("marketplace.multicall.aggregate.calls", report.aggregateCalls, {
                  hydration,
                  reason,
                });
                recordMetric("marketplace.multicall.low_level.calls", report.lowLevelCalls, {
                  hydration,
                  reason,
                });
                recordMetric("marketplace.multicall.partial_failures", report.partialFailures, {
                  hydration,
                  reason,
                });
                recordMetric("marketplace.multicall.fallback_tokens", report.fallbackTokens, {
                  hydration,
                  reason,
                });

                observabilitySummaryRef.current.multicall.aggregateCalls += report.aggregateCalls;
                observabilitySummaryRef.current.multicall.lowLevelCalls += report.lowLevelCalls;
                observabilitySummaryRef.current.multicall.partialFailures += report.partialFailures;
                observabilitySummaryRef.current.multicall.fallbackTokens += report.fallbackTokens;
                observabilitySummaryRef.current.multicall.durationMs = Number(elapsedTimer || elapsed || 0);
                observabilitySummaryRef.current.multicall.disabledReason = null;
              }

              if (MARKETPLACE_DEBUG) {
                const elapsed = Math.round(performance.now() - startedAt);
                console.log(
                  `[multicall] ${reason} ${hydration} hydration: ${result.stats.tokenCount} tokens, ${result.stats.lowLevelCalls} calls batched into ${result.stats.aggregateCalls} aggregate3 call(s), ${elapsed}ms, partialFailures=${result.stats.partialFailureCount}, fallback=${failedIds.length}`
                );
              }

              if (failedIds.length > 0) {
                await runWithConcurrency(
                  failedIds,
                  async (id) => {
                    if (!isSessionActive(sessionId)) return;
                    try {
                      if (hydration === "full") {
                        await hydrateFullToken(id, `${reason}:multicall-fallback`);
                      } else {
                        await hydrateDynamicToken(id, `${reason}:multicall-fallback`);
                      }
                    } catch (error) {
                      report.failedTokens += 1;
                      throw error;
                    }
                  },
                  concurrency
                );
              }

              return report;
            } catch (error) {
              report.mode = "fallback";
              report.fallbackTokens = normalizedIds.length;
              report.failedTokens += 1;

              if (MARKETPLACE_OBSERVABILITY_ENABLED) {
                endTimer(timerId, {
                  hydration,
                  reason,
                  mode: "fallback",
                  failure: true,
                });

                const msg = String(error?.message || "").toLowerCase();
                if (msg.includes("bad_data")) {
                  incrementCounter("marketplace.multicall.bad_data", { hydration, reason });
                  observabilitySummaryRef.current.multicall.badDataCount += 1;
                }
              }

              if (MARKETPLACE_DEBUG) {
                console.warn(
                  `[multicall] ${reason} ${hydration} failed; fallback to bounded-concurrency`,
                  error
                );
              }
            }
          } else {
            report.mode = "fallback";
            report.fallbackTokens = normalizedIds.length;
          }
        } else if (MARKETPLACE_DEBUG) {
          console.log(
            `[multicall] ${reason} ${hydration} fallback: ${multicallConfig.reason}`
          );
        }

        if (MARKETPLACE_OBSERVABILITY_ENABLED && !multicallConfig.configured) {
          incrementCounter("marketplace.multicall.disabled", {
            reason: multicallConfig.reason,
            hydration,
            mode: reason,
          });
          observabilitySummaryRef.current.multicall.disabledReason = multicallConfig.reason;
          report.mode = "fallback";
          report.fallbackTokens = normalizedIds.length;
        }
      }

      await runWithConcurrency(normalizedIds, async (id) => {
        try {
          if (hydration === "full") {
            await hydrateFullToken(id, reason);
          } else {
            await hydrateDynamicToken(id, reason);
          }
        } catch (error) {
          report.failedTokens += 1;
          throw error;
        }
      }, concurrency);

      return report;
    },
    [
      chainId,
      clearRetryState,
      currentAccount,
      fetchMetadataForTokenUri,
      hydrateDynamicToken,
      hydrateFullToken,
      isSessionActive,
      recoverAuctionStartBlock,
    ]
  );

  const queueDirtyToken = useCallback(
    (tokenId, hydration = "dynamic", reason = "event") => {
      const normalizedId = normalizeTokenId(tokenId);
      if (!normalizedId) return;

      const existing = dirtyTokenMapRef.current.get(normalizedId);
      if (!existing) {
        dirtyTokenMapRef.current.set(normalizedId, {
          hydration,
          reason,
          queuedAt: performance.now(),
        });
      } else {
        const resolvedHydration = existing.hydration === "full" || hydration === "full"
          ? "full"
          : "dynamic";

        const useIncomingReason = dirtyReasonPriority(reason) >= dirtyReasonPriority(existing.reason);
        dirtyTokenMapRef.current.set(normalizedId, {
          hydration: resolvedHydration,
          reason: useIncomingReason ? reason : existing.reason,
          queuedAt: existing.queuedAt || performance.now(),
        });
      }

      dispatch({ type: "MARK_DIRTY", payload: [normalizedId] });

      if (dirtyFlushTimerRef.current) {
        window.clearTimeout(dirtyFlushTimerRef.current);
      }

      const debounceMs = clampToPositiveInt(process.env.REACT_APP_MARKETPLACE_DIRTY_DEBOUNCE_MS, 400);
      dirtyFlushTimerRef.current = window.setTimeout(async () => {
        const sessionId = sessionIdRef.current;
        const entries = Array.from(dirtyTokenMapRef.current.entries());
        dirtyTokenMapRef.current.clear();
        dispatch({ type: "CLEAR_DIRTY", payload: entries.map(([id]) => id) });

        const reasonBuckets = {};
        entries.forEach(([, meta]) => {
          const normalizedReason = normalizeDirtyReason(meta?.reason);
          reasonBuckets[normalizedReason] = (reasonBuckets[normalizedReason] || 0) + 1;
        });

        let failedTokens = 0;
        let fallbackTokens = 0;
        const flushTimerId = MARKETPLACE_OBSERVABILITY_ENABLED
          ? startTimer("marketplace.dirty.flush.duration_ms", { tokens: entries.length })
          : null;

        if (MARKETPLACE_OBSERVABILITY_ENABLED) {
          incrementCounter("marketplace.dirty.flush.count");
          recordMetric("marketplace.dirty.flush.tokens", entries.length, {
            reasonCount: Object.keys(reasonBuckets).length,
          });
        }

        try {
          await runWithConcurrency(
            entries,
            async ([id, meta]) => {
              if (!isSessionActive(sessionId)) return;
              const dirtyReason = normalizeDirtyReason(meta?.reason);
              if (MARKETPLACE_OBSERVABILITY_ENABLED) {
                recordEvent("marketplace.dirty.flush.reason", { reason: dirtyReason });
              }

              try {
                if (meta.hydration === "full") {
                  await hydrateFullToken(id, `dirty:${meta.reason}`);
                } else {
                  await hydrateDynamicToken(id, `dirty:${meta.reason}`);
                }
              } catch (error) {
                failedTokens += 1;
                throw error;
              }

              if (String(meta?.reason || "").includes("multicall-fallback")) {
                fallbackTokens += 1;
              }

              if (MARKETPLACE_DEBUG) {
                const dt = Math.round(performance.now() - meta.queuedAt);
                const calls = meta.hydration === "full" ? "6+metadata" : "4";
                console.log(`[marketplace] event-to-card-update token=${id} in ${dt}ms calls=${calls}`);
              }
            },
            clampToPositiveInt(process.env.REACT_APP_MARKETPLACE_EVENT_RECONCILE_CONCURRENCY, 4)
          );
        } finally {
          if (MARKETPLACE_OBSERVABILITY_ENABLED) {
            const durationMs = Number(endTimer(flushTimerId, {
              failedTokens,
              fallbackTokens,
            }) || 0);

            recordMetric("marketplace.dirty.flush.failed_tokens", failedTokens);
            recordMetric("marketplace.dirty.flush.fallback_tokens", fallbackTokens);

            const dirtySummary = observabilitySummaryRef.current.dirtyFlush;
            dirtySummary.count += 1;
            dirtySummary.lastMs = durationMs;
            dirtySummary.lastTokens = entries.length;
            dirtySummary.lastFailedTokens = failedTokens;
            dirtySummary.lastFallbackTokens = fallbackTokens;
            dirtySummary.lastReason = Object.keys(reasonBuckets).sort((a, b) => reasonBuckets[b] - reasonBuckets[a])[0] || null;
            dirtySummary.avgMs = dirtySummary.count > 0
              ? ((dirtySummary.avgMs * (dirtySummary.count - 1)) + durationMs) / dirtySummary.count
              : durationMs;
          }
        }
      }, debounceMs);
    },
    [hydrateDynamicToken, hydrateFullToken, isSessionActive]
  );

  useEffect(() => {
    queueDirtyTokenRef.current = queueDirtyToken;
  }, [queueDirtyToken]);

  const processEventUpdate = useCallback(
    async (eventName, args, event, source = "live") => {
      await indexEvent(eventName, args, event, source, { applyPatch: true, queueHydration: true });
    },
    [indexEvent]
  );

  const processReceiptLogs = useCallback(
    async (receipt) => {
      if (!receipt || !Array.isArray(receipt.logs)) return;
      const { nft, marketplace } = contractsRef.current;
      if (!nft || !marketplace) return;

      traceMarketplaceEvent("processing receipt logs", {
        txHash: receipt.transactionHash || "",
        logs: receipt.logs.length,
      });

      for (const log of receipt.logs) {
        let parsed = null;
        let eventName = null;

        try {
          parsed = marketplace.interface.parseLog(log);
          eventName = parsed?.name || null;
        } catch {
          // Ignore and try NFT ABI
        }

        if (!parsed) {
          try {
            parsed = nft.interface.parseLog(log);
            eventName = parsed?.name || null;
          } catch {
            // Ignore unknown event logs
          }
        }

        if (!parsed || !eventName) continue;

        traceMarketplaceEvent("receipt event parsed", {
          eventName,
          blockNumber: Number(log.blockNumber || 0),
          logIndex: Number(log.logIndex ?? log.index ?? 0),
          txHash: log.transactionHash || "",
        });

        await processEventUpdate(
          eventName,
          parsed.args,
          {
            blockNumber: log.blockNumber,
            transactionHash: log.transactionHash,
            logIndex: log.logIndex ?? log.index ?? 0,
            index: log.index ?? log.logIndex ?? 0,
          },
          "receipt"
        );
      }
    },
    [processEventUpdate]
  );

  const replayEventsInRange = useCallback(
    async ({ trigger = "manual", fromBlockHint = null, toBlockHint = null, sessionId = null } = {}) => {
      const { provider, nft, marketplace } = contractsRef.current;
      const chunkSize = clampToPositiveInt(process.env.REACT_APP_MARKETPLACE_REPLAY_CHUNK_SIZE, 2000);
      const replayTimerId = MARKETPLACE_OBSERVABILITY_ENABLED
        ? startTimer("marketplace.replay.duration_ms", {
            trigger,
            fromBlockHint,
            toBlockHint,
          })
        : null;

      const replayAttempt = {
        trigger,
        status: "failed",
        failureCategory: null,
        latestBlock: null,
        cursorBefore: Number(eventCursorRef.current),
        cursorAfter: Number(eventCursorRef.current),
        fromBlock: null,
        toBlock: null,
        rangeBlocks: 0,
        lagBlocks: null,
        lagAgeSeconds: null,
        eventsProcessed: 0,
        affectedTokens: 0,
        durationMs: 0,
      };
      const affectedTokenIds = new Set();

      try {
        if (!provider || !nft || !marketplace) {
          replayAttempt.failureCategory = "contracts_unavailable";
          return;
        }

        if (sessionId != null && !isSessionActive(sessionId)) {
          replayAttempt.failureCategory = "session_inactive";
          return;
        }

        let latestBlock;
        try {
          latestBlock = Number(await provider.getBlockNumber());
        } catch (error) {
          replayAttempt.failureCategory = classifyReplayError(error, "latest_block_failed");
          return;
        }

        if (!Number.isFinite(latestBlock)) {
          replayAttempt.failureCategory = "latest_block_invalid";
          return;
        }

        replayAttempt.latestBlock = latestBlock;

        const currentCursor = Number(eventCursorRef.current);
        const normalizedCurrentCursor = Number.isFinite(currentCursor)
          ? Math.max(0, Math.floor(currentCursor))
          : null;

        let cursorBefore = normalizedCurrentCursor;
        if (!Number.isFinite(cursorBefore)) {
          const configuredStartBlock = Math.max(0, getConfiguredStartBlock());
          cursorBefore = Math.min(configuredStartBlock, latestBlock);
          setEventCursor(cursorBefore);
        }

        const hintedFrom = toOptionalBlockNumber(fromBlockHint);
        const hintedTo = toOptionalBlockNumber(toBlockHint);

        const fromBlock = Number.isFinite(hintedFrom)
          ? Math.max(0, hintedFrom)
          : cursorBefore + 1;
        const computedToBlock = Number.isFinite(hintedTo)
          ? Math.min(Math.max(0, hintedTo), latestBlock)
          : Math.min(latestBlock, fromBlock + chunkSize - 1);

        replayAttempt.cursorBefore = cursorBefore;
        replayAttempt.fromBlock = fromBlock;
        replayAttempt.toBlock = computedToBlock;
        replayAttempt.rangeBlocks = fromBlock <= computedToBlock ? (computedToBlock - fromBlock) + 1 : 0;

        traceMarketplaceEvent("replay start", {
          trigger,
          latestBlock,
          cursorBefore,
          fromBlock,
          toBlock: computedToBlock,
          rangeBlocks: replayAttempt.rangeBlocks,
        });

        if (fromBlock > latestBlock) {
          replayAttempt.status = "skipped_current";
          replayAttempt.toBlock = latestBlock;
          replayAttempt.rangeBlocks = 0;
        } else {
          if (computedToBlock < fromBlock || computedToBlock <= 0) {
            replayAttempt.status = "failed";
            replayAttempt.failureCategory = "invalid_range";
            traceMarketplaceEvent("replay invalid range; cursor preserved", {
              trigger,
              latestBlock,
              cursorBefore,
              fromBlock,
              toBlock: computedToBlock,
              rangeBlocks: replayAttempt.rangeBlocks,
            });
            return;
          }

          for (let chunkFrom = fromBlock; chunkFrom <= computedToBlock; chunkFrom += chunkSize) {
            if (sessionId != null && !isSessionActive(sessionId)) {
              replayAttempt.status = "failed";
              replayAttempt.failureCategory = "session_inactive";
              break;
            }

            const chunkTo = Math.min(chunkFrom + chunkSize - 1, computedToBlock);

            let batches;
            try {
              batches = await Promise.all([
                nft.queryFilter(nft.filters.Transfer(), chunkFrom, chunkTo),
                nft.queryFilter(nft.filters.CarbonCreditRetired(), chunkFrom, chunkTo),
                ...MARKETPLACE_EVENT_NAMES.map((eventName) => {
                  const filter = marketplace.filters[eventName]();
                  return marketplace.queryFilter(filter, chunkFrom, chunkTo);
                }),
              ]);
            } catch (error) {
              replayAttempt.status = "failed";
              replayAttempt.failureCategory = classifyReplayError(error, "query_failed");

              traceMarketplaceEvent("replay chunk failed; cursor preserved", {
                fromBlock: chunkFrom,
                toBlock: chunkTo,
                error: error?.message || String(error),
              });
              break;
            }

            const events = [];
            const [transferEvents, retiredEvents, ...marketplaceEvents] = batches;

            traceMarketplaceEvent("replay chunk fetched", {
              fromBlock: chunkFrom,
              toBlock: chunkTo,
              transfer: transferEvents.length,
              retired: retiredEvents.length,
              marketplace: MARKETPLACE_EVENT_NAMES.reduce((acc, eventName, idx) => {
                acc[eventName] = (marketplaceEvents[idx] || []).length;
                return acc;
              }, {}),
            });

            for (const e of transferEvents) events.push({ name: "Transfer", event: e });
            for (const e of retiredEvents) events.push({ name: "CarbonCreditRetired", event: e });
            MARKETPLACE_EVENT_NAMES.forEach((eventName, idx) => {
              for (const e of marketplaceEvents[idx] || []) {
                events.push({ name: eventName, event: e });
              }
            });

            events.sort((a, b) => {
              const blockDelta = Number(a.event?.blockNumber || 0) - Number(b.event?.blockNumber || 0);
              if (blockDelta !== 0) return blockDelta;
              return getEventLogIndex(a.event) - getEventLogIndex(b.event);
            });

            try {
              for (const item of events) {
                const tokenId = extractTokenIdFromEvent(item?.name, item?.event?.args);
                if (tokenId) affectedTokenIds.add(tokenId);
                await processEventUpdate(item.name, item.event.args, item.event, "replay");
              }
            } catch (error) {
              replayAttempt.status = "failed";
              replayAttempt.failureCategory = classifyReplayError(error, "process_failed");
              traceMarketplaceEvent("replay process failed; cursor preserved", {
                fromBlock: chunkFrom,
                toBlock: chunkTo,
                error: error?.message || String(error),
              });
              break;
            }

            replayAttempt.eventsProcessed += events.length;

            // Commit replay cursor even when the scanned chunk has zero relevant events.
            setEventCursor(chunkTo);
            traceMarketplaceEvent("replay chunk committed", {
              fromBlock: chunkFrom,
              toBlock: chunkTo,
              eventsProcessed: events.length,
              cursor: chunkTo,
            });
          }

          if (replayAttempt.status !== "failed") {
            replayAttempt.status = "success";
          }
        }

        replayAttempt.affectedTokens = affectedTokenIds.size;

        const cursorAfterRaw = Number(eventCursorRef.current);
        const cursorAfter = Number.isFinite(cursorAfterRaw)
          ? Math.max(0, Math.floor(cursorAfterRaw))
          : replayAttempt.cursorBefore;
        replayAttempt.cursorAfter = cursorAfter;
        replayAttempt.lagBlocks = Math.max(0, latestBlock - cursorAfter);

        const latestTs = Number(stateRef.current.blockTimestampByNumber?.[String(latestBlock)] || 0) || null;
        const cursorTs = Number(stateRef.current.blockTimestampByNumber?.[String(cursorAfter)] || 0) || null;
        replayAttempt.lagAgeSeconds = latestTs && cursorTs ? Math.max(0, latestTs - cursorTs) : null;
      } finally {
        const durationMs = MARKETPLACE_OBSERVABILITY_ENABLED
          ? Number(endTimer(replayTimerId, {
              trigger: replayAttempt.trigger,
              latestBlock: replayAttempt.latestBlock,
              cursorBefore: replayAttempt.cursorBefore,
              cursorAfter: replayAttempt.cursorAfter,
              fromBlock: replayAttempt.fromBlock,
              toBlock: replayAttempt.toBlock,
              rangeBlocks: replayAttempt.rangeBlocks,
              lagBlocks: replayAttempt.lagBlocks,
              eventsProcessed: replayAttempt.eventsProcessed,
              affectedTokens: replayAttempt.affectedTokens,
              status: replayAttempt.status,
              failureCategory: replayAttempt.failureCategory,
            }) || 0)
          : 0;

        replayAttempt.durationMs = durationMs;

        if (MARKETPLACE_OBSERVABILITY_ENABLED) {
          recordMetric("marketplace.replay.latest_block", replayAttempt.latestBlock);
          recordMetric("marketplace.replay.cursor_before", replayAttempt.cursorBefore);
          recordMetric("marketplace.replay.cursor_after", replayAttempt.cursorAfter);
          recordMetric("marketplace.replay.from_block", replayAttempt.fromBlock);
          recordMetric("marketplace.replay.to_block", replayAttempt.toBlock);
          recordMetric("marketplace.replay.range_blocks", replayAttempt.rangeBlocks);
          recordMetric("marketplace.replay.lag_blocks", replayAttempt.lagBlocks);
          setGauge("marketplace.replay.lag_blocks", replayAttempt.lagBlocks, {
            status: replayAttempt.status,
            trigger: replayAttempt.trigger,
          });
          recordMetric("marketplace.replay.events_processed", replayAttempt.eventsProcessed);
          recordMetric("marketplace.replay.affected_tokens", replayAttempt.affectedTokens);

          if (replayAttempt.status === "failed") {
            incrementCounter("marketplace.replay.failure", {
              category: replayAttempt.failureCategory || "unknown",
              trigger: replayAttempt.trigger,
              latestBlock: replayAttempt.latestBlock,
              cursorBefore: replayAttempt.cursorBefore,
              fromBlock: replayAttempt.fromBlock,
              toBlock: replayAttempt.toBlock,
            });
          }

          observabilitySummaryRef.current.replay = {
            latestBlock: replayAttempt.latestBlock,
            cursorBefore: replayAttempt.cursorBefore,
            cursorAfter: replayAttempt.cursorAfter,
            fromBlock: replayAttempt.fromBlock,
            toBlock: replayAttempt.toBlock,
            lagBlocks: replayAttempt.lagBlocks,
            lagAgeSeconds: replayAttempt.lagAgeSeconds,
            durationMs,
            rangeBlocks: replayAttempt.rangeBlocks,
            eventsProcessed: replayAttempt.eventsProcessed,
            affectedTokens: replayAttempt.affectedTokens,
            failed: replayAttempt.status === "failed",
            status: replayAttempt.status,
            failureCategory: replayAttempt.failureCategory,
          };
        }

        logReplayAttempt(replayAttempt);
      }
    },
    [isSessionActive, processEventUpdate, setEventCursor]
  );

  const discoverMintedTokenIds = useCallback(async () => {
    const { provider, nft } = contractsRef.current;
    if (!provider || !nft) return [];

    const latestBlock = Number(await provider.getBlockNumber());
    const cachedMintScan = stateRef.current.lastScannedBlocks?.mint;

    const configuredStart = Number(
      process.env.REACT_APP_NFT_START_BLOCK ||
        process.env.REACT_APP_MARKETPLACE_START_BLOCK ||
        process.env.SAVED_BLOCK ||
        NaN
    );

    const lookback = clampToPositiveInt(process.env.REACT_APP_NFT_LOOKBACK_BLOCKS, 250000);
    const fallbackStart = Math.max(0, latestBlock - lookback);
    const scanStart = Number.isFinite(cachedMintScan)
      ? Math.min(cachedMintScan + 1, latestBlock)
      : Number.isFinite(configuredStart)
        ? Math.max(Math.min(configuredStart, latestBlock), fallbackStart)
        : fallbackStart;

    const discovered = new Set(stateRef.current.tokenOrder);
    const chunkSize = clampToPositiveInt(process.env.REACT_APP_LOG_CHUNK_SIZE, 2000);

    if (scanStart <= latestBlock) {
      for (let from = scanStart; from <= latestBlock; from += chunkSize) {
        const to = Math.min(from + chunkSize - 1, latestBlock);
        const mintEvents = await nft.queryFilter(nft.filters.Transfer(ZERO_ADDRESS, null), from, to).catch(() => []);
        for (const event of mintEvents) {
          const tokenId = normalizeTokenId(event.args?.tokenId ?? event.args?.[2]);
          if (tokenId) discovered.add(tokenId);
        }
      }
    }

    dispatch({ type: "SET_LAST_SCANNED_BLOCK", payload: { mint: latestBlock } });
    return Array.from(discovered);
  }, []);

  const hydrateVisiblePage = useCallback(
    async (reason = "visible") => {
      const visibleIds = paginatedTokenIds;
      if (!visibleIds.length) return;

      const start = performance.now();
      const timerId = MARKETPLACE_OBSERVABILITY_ENABLED
        ? startTimer("marketplace.hydration.visible.duration_ms", { reason, visibleTokens: visibleIds.length })
        : null;
      let timerClosed = false;

      dispatch({ type: "SET_LOADING", payload: { visibleHydration: true } });

      const needsHydration = visibleIds.filter((tokenId) => {
        const token = stateRef.current.tokensById[tokenId];
        if (!token) return true;
        if (!token.fullHydratedAt) return true;
        return token.metadataStatus !== "cached" && token.metadataStatus !== "loaded";
      });

      const needsDynamicOnly = visibleIds.filter((tokenId) => !needsHydration.includes(tokenId));

      try {
        const fullReport = await hydrateTokenIds(
          needsHydration,
          "full",
          reason,
          clampToPositiveInt(process.env.REACT_APP_MARKETPLACE_VISIBLE_CONCURRENCY, 6)
        );
        const dynamicReport = await hydrateTokenIds(
          needsDynamicOnly,
          "dynamic",
          `${reason}-dynamic`,
          clampToPositiveInt(process.env.REACT_APP_MARKETPLACE_VISIBLE_DYNAMIC_CONCURRENCY, 6)
        );

        if (MARKETPLACE_OBSERVABILITY_ENABLED) {
          const reports = [fullReport, dynamicReport].filter(Boolean);
          const mode = chooseHydrationMode(reports);
          const fallbackTokens = reports.reduce((acc, item) => acc + Number(item?.fallbackTokens || 0), 0);
          const failedTokens = reports.reduce((acc, item) => acc + Number(item?.failedTokens || 0), 0);
          const lowLevelReads = reports.reduce((acc, item) => {
            const known = Number(item?.lowLevelCalls || 0);
            if (known > 0) return acc + known;
            const perToken = item?.mode === "multicall" || item?.mode === "fallback"
              ? (item?.tokenCount || 0)
              : 0;
            return acc + perToken;
          }, 0);

          const durationMs = Number(endTimer(timerId, {
            mode,
            fallbackTokens,
            failedTokens,
          }) || (performance.now() - start));
          timerClosed = true;

          recordMetric("marketplace.hydration.visible.tokens", visibleIds.length, { reason });
          incrementCounter("marketplace.hydration.visible.mode", { mode, reason });
          recordMetric("marketplace.hydration.visible.fallback_tokens", fallbackTokens, { reason });
          recordMetric("marketplace.hydration.visible.failed_tokens", failedTokens, { reason });

          observabilitySummaryRef.current.visibleHydration = {
            durationMs,
            tokens: visibleIds.length,
            mode,
            fallbackTokens,
            failedTokens,
            lowLevelReads,
          };

          if (firstVisibleHydrationMsRef.current == null) {
            firstVisibleHydrationMsRef.current = durationMs;
            recordMetric("marketplace.hydration.visible.first_render_ms", durationMs, { reason });
          }
        }

        if (MARKETPLACE_PERF_DEBUG) {
          console.log(`[perf] visible hydration completed in ${Math.round(performance.now() - start)}ms`);
        }
      } finally {
        if (MARKETPLACE_OBSERVABILITY_ENABLED && !timerClosed && timerId) {
          endTimer(timerId, { failed: true, reason });
        }
        dispatch({ type: "SET_LOADING", payload: { visibleHydration: false, staleFromCache: false } });
      }
    },
    [hydrateTokenIds, paginatedTokenIds]
  );

  const hydrateBackgroundTokens = useCallback(async () => {
    if (backgroundHydrationRef.current.inFlight) return;

    const allIds = stateRef.current.tokenOrder || [];
    if (!allIds.length) return;

    const visibleSet = new Set(paginatedTokenIds);
    const backgroundIds = allIds.filter((id) => !visibleSet.has(id));
    if (!backgroundIds.length) return;

    const targets = backgroundIds.filter((id) => {
      const token = stateRef.current.tokensById[id];
      if (!token) return true;
      if (token.hiddenFromMarketplace || token.metadataStatus === "missing-uri") return false;
      if (!token.fullHydratedAt) return true;
      return token.metadataStatus !== "cached" && token.metadataStatus !== "loaded";
    });

    if (!targets.length) return;

    const targetKey = targets.join(",");
    if (targetKey && backgroundHydrationRef.current.key === targetKey) {
      return;
    }

    const start = performance.now();
    const timerId = MARKETPLACE_OBSERVABILITY_ENABLED
      ? startTimer("marketplace.hydration.background.duration_ms", { tokens: targets.length })
      : null;
    let timerClosed = false;
    let aborted = false;
    backgroundHydrationRef.current.inFlight = true;
    backgroundHydrationRef.current.key = targetKey;
    dispatch({ type: "SET_LOADING", payload: { backgroundHydration: true } });

    try {
      const report = await hydrateTokenIds(
        targets,
        "full",
        "background",
        clampToPositiveInt(process.env.REACT_APP_MARKETPLACE_BACKGROUND_CONCURRENCY, 3)
      );

      if (MARKETPLACE_OBSERVABILITY_ENABLED) {
        const mode = report?.mode || "bounded_concurrency";
        const fallbackTokens = Number(report?.fallbackTokens || 0);
        const failedTokens = Number(report?.failedTokens || 0);
        const durationMs = Number(endTimer(timerId, {
          mode,
          fallbackTokens,
          failedTokens,
        }) || (performance.now() - start));
        timerClosed = true;

        recordMetric("marketplace.hydration.background.tokens", targets.length, { mode });
        recordMetric("marketplace.hydration.background.batches", 1, { mode });
        recordMetric("marketplace.hydration.background.fallback_tokens", fallbackTokens, { mode });
        recordMetric("marketplace.hydration.background.failed_tokens", failedTokens, { mode });
        recordMetric("marketplace.hydration.background.aborted", 0, { mode });

        observabilitySummaryRef.current.backgroundHydration = {
          durationMs,
          tokens: targets.length,
          batches: 1,
          mode,
          fallbackTokens,
          failedTokens,
          aborted: false,
        };
      }
    } catch (error) {
      aborted = String(error?.message || "").toLowerCase().includes("abort");
      if (MARKETPLACE_OBSERVABILITY_ENABLED) {
        recordMetric("marketplace.hydration.background.aborted", aborted ? 1 : 0, {
          mode: "failure",
        });
      }
      throw error;
    } finally {
      if (MARKETPLACE_OBSERVABILITY_ENABLED && !timerClosed && timerId) {
        const durationMs = Number(endTimer(timerId, { failed: true, aborted }) || (performance.now() - start));
        observabilitySummaryRef.current.backgroundHydration = {
          durationMs,
          tokens: targets.length,
          batches: 1,
          mode: "fallback",
          fallbackTokens: targets.length,
          failedTokens: 1,
          aborted,
        };
      }

      backgroundHydrationRef.current.inFlight = false;
      dispatch({ type: "SET_LOADING", payload: { backgroundHydration: false } });
    }

    if (MARKETPLACE_PERF_DEBUG) {
      console.log(`[perf] background hydration completed in ${Math.round(performance.now() - start)}ms`);
    }
  }, [hydrateTokenIds, paginatedTokenIds]);

  const recomputeOwnershipForAccount = useCallback(
    (account) => {
      const accountLower = normalizeAddress(account);
      const patches = [];

      for (const tokenId of stateRef.current.tokenOrder) {
        const token = stateRef.current.tokensById[tokenId];
        if (!token || !token.owner) continue;
        patches.push({
          tokenId,
          isOwner: Boolean(accountLower && normalizeAddress(token.owner) === accountLower),
        });
      }

      if (patches.length) {
        dispatch({ type: "UPSERT_TOKENS", payload: patches });
      }

      const visibleWithMissingOwner = paginatedTokenIds.filter((id) => {
        const token = stateRef.current.tokensById[id];
        return token && !token.owner;
      });

      if (visibleWithMissingOwner.length) {
        hydrateTokenIds(visibleWithMissingOwner, "dynamic", "account-change", 4);
      }
    },
    [hydrateTokenIds, paginatedTokenIds]
  );

  useEffect(() => {
    recomputeOwnershipForAccountRef.current = recomputeOwnershipForAccount;
  }, [recomputeOwnershipForAccount]);

  const optimisticPatchToken = useCallback((tokenId, patch) => {
    dispatch({ type: "MERGE_TOKEN", payload: { tokenId, patch } });
  }, []);

  const fetchAuditHistoryForToken = useCallback(async (tokenId) => {
    const normalizedId = normalizeTokenId(tokenId);
    if (!normalizedId) return [];

    const { provider } = contractsRef.current;
    if (!provider) return [];

    const latestBlock = Number(await provider.getBlockNumber().catch(() => NaN));
    if (!Number.isFinite(latestBlock)) return [];

    const indexed = stateRef.current.eventsByTokenId?.[normalizedId] || [];
    const latestIndexedBlock = indexed.length
      ? Number(indexed[indexed.length - 1]?.blockNumber || 0)
      : null;

    const configuredStart = getConfiguredStartBlock();
    const overlapBlocks = clampToPositiveInt(
      process.env.REACT_APP_MARKETPLACE_HISTORY_BACKFILL_OVERLAP_BLOCKS,
      2500
    );
    const fromBlock = Number.isFinite(latestIndexedBlock)
      ? Math.max(configuredStart, latestIndexedBlock - overlapBlocks)
      : configuredStart;

    let historySource = "event-index";
    let backfilledCount = 0;

    if (fromBlock <= latestBlock) {
      const missingEvents = await queryTokenEventsBounded(normalizedId, fromBlock, latestBlock);
      backfilledCount = missingEvents.length;
      if (backfilledCount > 0) {
        historySource = "event-index+bounded-backfill";
      }
      for (const item of missingEvents) {
        await indexEvent(item.name, item.event.args, item.event, "history-backfill", {
          applyPatch: false,
          queueHydration: false,
        });
      }
    }

    const indexedEvents = stateRef.current.eventsByTokenId?.[normalizedId] || [];

    const eventsByTx = new Map();
    indexedEvents.forEach((eventItem) => {
      const txHash = String(eventItem?.transactionHash || "");
      if (!txHash) return;
      const list = eventsByTx.get(txHash) || [];
      list.push(eventItem);
      eventsByTx.set(txHash, list);
    });

    const filteredIndexedEvents = indexedEvents.filter((eventItem) => {
      if (eventItem?.eventName !== "Transfer") return true;

      const txHash = String(eventItem?.transactionHash || "");
      if (!txHash) return true;

      const siblings = eventsByTx.get(txHash) || [];
      const hasPurchase = siblings.some((entry) => entry?.eventName === "TokenPurchased");
      const hasAuctionEnded = siblings.some((entry) => entry?.eventName === "AuctionEnded");
      if (!hasPurchase && !hasAuctionEnded) return true;

      const args = eventItem?.args || {};
      const from = normalizeAddress(args?.from ?? args?.[0]);
      if (from === ZERO_ADDRESS) return true;

      return false;
    });

    const events = filteredIndexedEvents
      .map(formatHistoryEntry)
      .sort((a, b) => {
        const ts = Number(b.timestamp || 0) - Number(a.timestamp || 0);
        if (ts !== 0) return ts;
        return Number(b.blockNumber || 0) - Number(a.blockNumber || 0);
      });

    if (MARKETPLACE_DEBUG) {
      console.log(
        `[marketplace] history-source token=${normalizedId} source=${historySource} indexed=${indexed.length} backfilled=${backfilledCount} overlap=${overlapBlocks} from=${fromBlock} latest=${latestBlock}`
      );
    }

    return events;
  }, [indexEvent, queryTokenEventsBounded]);

  const fetchBidHistoryForToken = useCallback(
    async (tokenId, auctionStartBlock) => {
      const normalizedId = normalizeTokenId(tokenId);
      if (!normalizedId) return [];

      const { provider, marketplace } = contractsRef.current;
      if (!provider || !marketplace) return [];

      const token = stateRef.current.tokensById[normalizedId] || {};
      let startBlock = Number(auctionStartBlock ?? token?.auctionData?.auctionStartBlock);

      if (!Number.isFinite(startBlock) || startBlock <= 0) {
        startBlock = Number(await recoverAuctionStartBlock(normalizedId));
      }

      if (!Number.isFinite(startBlock) || startBlock <= 0) {
        startBlock = getConfiguredStartBlock();
      }

      const cacheKey = `${normalizedId}:${startBlock}:v2`;
      const cached = stateRef.current.bidHistoryByKey[cacheKey];
      const isAuctionActive = Boolean(token?.isAuction || token?.auctionData?.active);
      const indexedHasEnded = (stateRef.current.eventsByTokenId?.[normalizedId] || []).some((eventItem) => {
        if (eventItem?.eventName !== "AuctionEnded") return false;
        const block = Number(eventItem?.blockNumber || 0);
        return Number.isFinite(block) && block >= startBlock;
      });
      const cachedHasEnded = Array.isArray(cached) && cached.some((entry) => entry?.type === "ended");

      // If auction is inactive and we already know there's no missing ended marker, cache can be used directly.
      if (!isAuctionActive && Array.isArray(cached) && cached.length > 0 && (!indexedHasEnded || cachedHasEnded)) {
        if (MARKETPLACE_DEBUG) {
          console.log(
            `[marketplace] bid-history-source token=${normalizedId} source=cache-hit cacheKey=${cacheKey} entries=${cached.length}`
          );
        }
        return cached;
      }

      const latestBlock = Number(await provider.getBlockNumber().catch(() => NaN));
      let sourceEvents = [];
      let sourceLabel = "bounded-auction-query";
      let startedEventsCount = 0;
      let bidEventsCount = 0;
      let endedEventsCount = 0;

      if (Number.isFinite(latestBlock) && startBlock <= latestBlock) {
        const [startedEvents, bidEvents, endedEvents] = await Promise.all([
          marketplace.queryFilter(marketplace.filters.AuctionStarted(normalizedId), startBlock, latestBlock).catch(() => []),
          marketplace.queryFilter(marketplace.filters.NewHighestBid(normalizedId), startBlock, latestBlock).catch(() => []),
          marketplace.queryFilter(marketplace.filters.AuctionEnded(normalizedId), startBlock, latestBlock).catch(() => []),
        ]);

        startedEventsCount = startedEvents.length;
        bidEventsCount = bidEvents.length;
        endedEventsCount = endedEvents.length;

        for (const event of startedEvents) {
          await indexEvent("AuctionStarted", event.args, event, "bid-history-backfill", {
            applyPatch: false,
            queueHydration: false,
          });
          sourceEvents.push({ name: "AuctionStarted", event });
        }

        for (const event of bidEvents) {
          await indexEvent("NewHighestBid", event.args, event, "bid-history-backfill", {
            applyPatch: false,
            queueHydration: false,
          });
          sourceEvents.push({ name: "NewHighestBid", event });
        }

        for (const event of endedEvents) {
          await indexEvent("AuctionEnded", event.args, event, "bid-history-backfill", {
            applyPatch: false,
            queueHydration: false,
          });
          sourceEvents.push({ name: "AuctionEnded", event });
        }
      }

      if (sourceEvents.length === 0) {
        const fromIndex = (stateRef.current.eventsByTokenId?.[normalizedId] || [])
          .filter((eventItem) => {
            if (
              eventItem.eventName !== "AuctionStarted" &&
              eventItem.eventName !== "NewHighestBid" &&
              eventItem.eventName !== "AuctionEnded"
            ) return false;
            const block = Number(eventItem.blockNumber || 0);
            return Number.isFinite(block) && block >= startBlock;
          })
          .map((eventItem) => ({
            name: eventItem.eventName,
            event: eventItem,
          }));

        sourceEvents = fromIndex;
        sourceLabel = "event-index";
      }

      sourceEvents.sort((a, b) => {
        const blockDelta = Number(a.event?.blockNumber || 0) - Number(b.event?.blockNumber || 0);
        if (blockDelta !== 0) return blockDelta;
        return getEventLogIndex(a.event) - getEventLogIndex(b.event);
      });

      const pickArg = (args, ...keys) => {
        for (const key of keys) {
          const value = args?.[key];
          if (value !== undefined && value !== null && value !== "") {
            return value;
          }
        }
        return null;
      };

      const history = [];
      for (const item of sourceEvents) {
        const eventName = item?.name || item?.event?.eventName || "";
        const eventItem = item?.event || {};
        const args = eventItem.args || {};
        const blockNumber = Number(eventItem.blockNumber || 0);

        let timestamp = Number(eventItem.timestamp || 0) || null;
        if (!timestamp && Number.isFinite(blockNumber) && blockNumber > 0) {
          timestamp = await getBlockTimestampCached(blockNumber);
        }

        const isEnded = eventName === "AuctionEnded";
        const isStart = eventName === "AuctionStarted";
        const bidder = isStart
          ? pickArg(args, "seller", 1)
          : isEnded
            ? pickArg(args, "winner", 1)
            : pickArg(args, "bidder", 1);
        const amount = isStart
          ? pickArg(args, "minPrice", "startingPrice", 2)
          : pickArg(args, "amount", 2);

        history.push({
          type: isStart ? "start" : isEnded ? "ended" : "bid",
          time: timestamp
            ? new Date(Number(timestamp) * 1000).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })
            : "--:--:--",
          bidder: bidder || "",
          amount: amount ?? 0,
          txHash: eventItem.transactionHash,
          blockNumber: eventItem.blockNumber,
        });
      }

      history.reverse();

      if (history.length === 0 && Array.isArray(cached) && cached.length > 0) {
        if (MARKETPLACE_DEBUG) {
          console.log(
            `[marketplace] bid-history-source token=${normalizedId} source=cache-fallback cacheKey=${cacheKey} entries=${cached.length}`
          );
        }
        return cached;
      }

      if (MARKETPLACE_DEBUG) {
        console.log(
          `[marketplace] bid-history-source token=${normalizedId} source=${sourceLabel} cacheKey=${cacheKey} startBlock=${startBlock} latestBlock=${latestBlock} starts=${startedEventsCount} bids=${bidEventsCount} ended=${endedEventsCount} entries=${history.length}`
        );
      }

      dispatch({ type: "SET_BID_HISTORY", payload: { key: cacheKey, history } });
      return history;
    },
    [getBlockTimestampCached, indexEvent, recoverAuctionStartBlock]
  );

  const bootstrap = useCallback(async () => {
    const { provider, nft, marketplace } = contractsRef.current;
    if (!provider || !nft || !marketplace || !cacheKeyRef.current) return;

    const sessionId = sessionIdRef.current;
    const cacheTimerId = MARKETPLACE_OBSERVABILITY_ENABLED
      ? startTimer("marketplace.cache.restore.duration_ms", { cacheKey: cacheKeyRef.current })
      : null;
    let cacheTimerClosed = false;
    let cacheRestoreStatus = "no-cache";
    let cacheSizeKb = 0;
    let tokensRestored = 0;
    let eventsRestored = 0;
    let metadataRestored = 0;

    bootstrappingOwnerRef.current = sessionId;
    dispatch({ type: "SET_LOADING", payload: { bootstrapping: true } });

    try {
      const cacheRaw = window.localStorage.getItem(cacheKeyRef.current);
      cacheSizeKb = cacheRaw ? Number((cacheRaw.length / 1024).toFixed(2)) : 0;
      const cached = safeParseJson(cacheRaw, null);

      if (cached && cached.schemaVersion === MARKETPLACE_CACHE_SCHEMA_VERSION) {
        dispatch({ type: "HYDRATE_FROM_CACHE", payload: cached.state });
        eventCursorRef.current = cached?.state?.eventCursor?.lastProcessedEventBlock ?? null;

        const cachedEvents = cached?.state?.eventsByTokenId || {};
        Object.values(cachedEvents).forEach((list) => {
          (list || []).forEach((item) => {
            if (item?.eventKey) {
              seenEventsRef.current.add(item.eventKey);
            }
          });
        });

        tokensRestored = Object.keys(cached?.state?.tokensById || {}).length;
        metadataRestored = Object.keys(cached?.state?.metadataByUri || {}).length;
        eventsRestored = Object.values(cached?.state?.eventsByTokenId || {}).reduce((acc, list) => {
          return acc + (Array.isArray(list) ? list.length : 0);
        }, 0);
        cacheRestoreStatus = "success";

        if (MARKETPLACE_OBSERVABILITY_ENABLED) {
          incrementCounter("marketplace.cache.restore.success");
          recordMetric("marketplace.cache.size_kb", cacheSizeKb);
          recordMetric("marketplace.cache.tokens_restored", tokensRestored);
          recordMetric("marketplace.cache.events_restored", eventsRestored);
        }

        if (MARKETPLACE_PERF_DEBUG) {
          console.log("[perf] cache snapshot rendered immediately");
        }
      } else if (cacheRaw) {
        cacheRestoreStatus = cached ? "schema-mismatch" : "corrupt";
        window.localStorage.removeItem(cacheKeyRef.current);
        if (MARKETPLACE_OBSERVABILITY_ENABLED) {
          incrementCounter("marketplace.cache.restore.failure", { status: cacheRestoreStatus });
          recordEvent("marketplace.cache.restore.discarded", {
            cacheKey: cacheKeyRef.current,
            status: cacheRestoreStatus,
            sizeKb: cacheSizeKb,
          });
        }
      }

      if (MARKETPLACE_OBSERVABILITY_ENABLED) {
        const durationMs = Number(endTimer(cacheTimerId, {
          status: cacheRestoreStatus,
          tokensRestored,
          eventsRestored,
          metadataRestored,
        }) || 0);
        cacheTimerClosed = true;
        observabilitySummaryRef.current.cacheRestore = {
          durationMs,
          cacheKey: cacheKeyRef.current,
          sizeKb: cacheSizeKb,
          tokensRestored,
          eventsRestored,
          metadataRestored,
          status: cacheRestoreStatus,
        };
      }

      if (!isSessionActive(sessionId)) return;

      const tokenIds = await discoverMintedTokenIds();
      if (!isSessionActive(sessionId)) return;

      dispatch({ type: "ADD_TOKEN_IDS", payload: tokenIds });

      const fromBlock = Number(eventCursorRef.current);
      await replayEventsInRange({
        trigger: "bootstrap",
        fromBlockHint: Number.isFinite(fromBlock) ? fromBlock + 1 : null,
        sessionId,
      });
    } catch (error) {
      if (MARKETPLACE_OBSERVABILITY_ENABLED) {
        incrementCounter("marketplace.cache.restore.failure", { status: "exception" });
      }
      if (isSessionActive(sessionId)) {
        dispatch({
          type: "SET_GLOBAL_ERROR",
          payload: error?.message || "Failed to bootstrap marketplace snapshot",
        });
      }
    } finally {
      if (MARKETPLACE_OBSERVABILITY_ENABLED && !cacheTimerClosed && cacheTimerId) {
        const durationMs = Number(endTimer(cacheTimerId, {
          status: cacheRestoreStatus || "failure",
          tokensRestored,
          eventsRestored,
          metadataRestored,
        }) || 0);
        observabilitySummaryRef.current.cacheRestore = {
          durationMs,
          cacheKey: cacheKeyRef.current,
          sizeKb: cacheSizeKb,
          tokensRestored,
          eventsRestored,
          metadataRestored,
          status: cacheRestoreStatus || "failure",
        };
      }

      if (bootstrappingOwnerRef.current === sessionId) {
        bootstrappingOwnerRef.current = null;
        dispatch({ type: "SET_LOADING", payload: { bootstrapping: false } });
      }
    }
  }, [discoverMintedTokenIds, isSessionActive, replayEventsInRange]);

  useEffect(() => {
    if (!window.ethereum || !nftAddress || !marketplaceAddress) return undefined;

    let cancelled = false;
    const nextSession = sessionIdRef.current + 1;
    sessionIdRef.current = nextSession;
    cancelAllInFlightWork();

    const initialize = async () => {
      try {
        dispatch({ type: "SET_GLOBAL_ERROR", payload: null });

        const provider = new BrowserProvider(window.ethereum);
        const [network, accounts] = await Promise.all([
          provider.getNetwork(),
          provider.send("eth_accounts", []),
        ]);

        if (cancelled || !isSessionActive(nextSession)) return;

        const normalizedChainId = Number(network.chainId);
        const activeAccount = accounts?.[0] || null;

        setChainId(normalizedChainId);
        setCurrentAccount(activeAccount);

        const nft = new Contract(nftAddress, nftAbi, provider);
        const marketplace = new Contract(marketplaceAddress, marketplaceAbi, provider);

        contractsRef.current = { provider, nft, marketplace };
      } catch (error) {
        if (!cancelled) {
          dispatch({
            type: "SET_GLOBAL_ERROR",
            payload: error?.message || "Failed to initialize marketplace contracts",
          });
        }
      }
    };

    initialize();

    const handleAccountsChanged = (accounts) => {
      const nextAccount = accounts?.[0] || null;
      setCurrentAccount(nextAccount);
      recomputeOwnershipForAccountRef.current?.(nextAccount);
    };

    const handleChainChanged = () => {
      sessionIdRef.current += 1;
      cancelAllInFlightWork();
      dispatch({ type: "RESET" });
      eventCursorRef.current = null;
      seenEventsRef.current.clear();
      multicallProbeRef.current = { key: null, usable: null };
      setReinitNonce((value) => value + 1);
    };

    window.ethereum.on("accountsChanged", handleAccountsChanged);
    window.ethereum.on("chainChanged", handleChainChanged);

    return () => {
      cancelled = true;
      sessionIdRef.current += 1;
      cancelAllInFlightWork();
      window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
      window.ethereum.removeListener("chainChanged", handleChainChanged);
    };
  }, [cancelAllInFlightWork, isSessionActive, marketplaceAbi, marketplaceAddress, nftAbi, nftAddress, reinitNonce]);

  useEffect(() => {
    cacheKeyRef.current = cacheKey;
  }, [cacheKey]);

  useEffect(() => {
    if (!cacheKey || !contractsRef.current.provider) return;
    bootstrap();
  }, [bootstrap, cacheKey]);

  useEffect(() => {
    if (!contractsRef.current.provider || paginatedTokenIds.length === 0) return;

    const key = `${currentAccount || "anon"}:${activeTab}:${sortBy}:${sortOrder}:${currentPage}:${itemsPerPage}:${paginatedTokenIds.join(",")}`;
    if (lastVisibleHydrationKeyRef.current === key) return;
    lastVisibleHydrationKeyRef.current = key;

    hydrateVisiblePage("visible-change").catch(() => {
      // handled in token errors
    });
  }, [
    activeTab,
    sortBy,
    sortOrder,
    currentPage,
    itemsPerPage,
    paginatedTokenIds,
    hydrateVisiblePage,
    currentAccount,
  ]);

  useEffect(() => {
    if (!contractsRef.current.provider || !state.tokenOrder.length) return;
    const timer = window.setTimeout(() => {
      hydrateBackgroundTokens();
    }, 150);
    return () => window.clearTimeout(timer);
  }, [state.tokenOrder, hydrateBackgroundTokens]);

  useEffect(() => {
    if (!contractsRef.current.provider || !state.tokenOrder.length) return undefined;

    const intervalMs = clampToPositiveInt(process.env.REACT_APP_MARKETPLACE_RECONCILE_POLL_MS, 15000);
    const rollingBatchSize = clampToPositiveInt(process.env.REACT_APP_MARKETPLACE_RECONCILE_BATCH_SIZE, 24);
    const concurrency = clampToPositiveInt(process.env.REACT_APP_MARKETPLACE_RECONCILE_CONCURRENCY, 4);

    const timer = window.setInterval(async () => {
      if (periodicReconcileRef.current.inFlight) return;

      const allIds = stateRef.current.tokenOrder || [];
      if (!allIds.length) return;

      const start = reconcileCursorRef.current % allIds.length;
      const rollingIds = [];
      for (let i = 0; i < Math.min(rollingBatchSize, allIds.length); i += 1) {
        rollingIds.push(allIds[(start + i) % allIds.length]);
      }
      reconcileCursorRef.current = (start + rollingIds.length) % allIds.length;

      const listedOrAuctionIds = allIds.filter((id) => {
        const token = stateRef.current.tokensById[id];
        if (!token) return false;
        return Boolean(token.isListed || token.isAuction || token.auctionData?.active);
      });

      const targetIds = Array.from(new Set([...paginatedTokenIds, ...listedOrAuctionIds, ...rollingIds]));
      if (!targetIds.length) return;

      periodicReconcileRef.current.inFlight = true;
      try {
        await hydrateTokenIds(targetIds, "dynamic", "periodic-reconcile", concurrency);
      } catch {
        // Token-level retry logic handles transient failures.
      } finally {
        periodicReconcileRef.current.inFlight = false;
      }
    }, intervalMs);

    return () => {
      window.clearInterval(timer);
    };
  }, [hydrateTokenIds, paginatedTokenIds, state.tokenOrder.length]);

  useEffect(() => {
    const { nft, marketplace, provider } = contractsRef.current;
    if (!nft || !marketplace || !provider) return undefined;

    const sessionId = sessionIdRef.current;

    const onTransfer = (from, to, tokenId, event) => {
      traceMarketplaceEvent("live event", {
        eventName: "Transfer",
        tokenId: normalizeTokenId(tokenId),
        blockNumber: Number(event?.blockNumber || 0),
        txHash: event?.transactionHash || "",
      });
      indexEvent("Transfer", { from, to, tokenId }, event, "live", { applyPatch: true, queueHydration: true });
    };

    const onRetired = (...params) => {
      const event = params[params.length - 1];
      const [tokenId, retiredBy] = params;
      traceMarketplaceEvent("live event", {
        eventName: "CarbonCreditRetired",
        tokenId: normalizeTokenId(tokenId),
        blockNumber: Number(event?.blockNumber || 0),
        txHash: event?.transactionHash || "",
      });
      indexEvent("CarbonCreditRetired", { tokenId, retiredBy }, event, "live", { applyPatch: true, queueHydration: true });
    };

    const onTokenListed = (...params) => {
      const event = params[params.length - 1];
      const [tokenId, seller, price] = params;
      traceMarketplaceEvent("live event", {
        eventName: "TokenListed",
        tokenId: normalizeTokenId(tokenId),
        blockNumber: Number(event?.blockNumber || 0),
        txHash: event?.transactionHash || "",
      });
      indexEvent("TokenListed", { tokenId, seller, price }, event, "live", { applyPatch: true, queueHydration: true });
    };

    const onListingCancelled = (...params) => {
      const event = params[params.length - 1];
      const [tokenId] = params;
      traceMarketplaceEvent("live event", {
        eventName: "ListingCancelled",
        tokenId: normalizeTokenId(tokenId),
        blockNumber: Number(event?.blockNumber || 0),
        txHash: event?.transactionHash || "",
      });
      indexEvent("ListingCancelled", { tokenId }, event, "live", { applyPatch: true, queueHydration: true });
    };

    const onTokenPurchased = (...params) => {
      const event = params[params.length - 1];
      const [tokenId, buyer, price] = params;
      traceMarketplaceEvent("live event", {
        eventName: "TokenPurchased",
        tokenId: normalizeTokenId(tokenId),
        blockNumber: Number(event?.blockNumber || 0),
        txHash: event?.transactionHash || "",
      });
      indexEvent("TokenPurchased", { tokenId, buyer, price }, event, "live", { applyPatch: true, queueHydration: true });
    };

    const onAuctionStarted = (...params) => {
      const event = params[params.length - 1];
      const [tokenId, seller, minPrice, endTime] = params;
      traceMarketplaceEvent("live event", {
        eventName: "AuctionStarted",
        tokenId: normalizeTokenId(tokenId),
        blockNumber: Number(event?.blockNumber || 0),
        txHash: event?.transactionHash || "",
      });
      indexEvent("AuctionStarted", { tokenId, seller, minPrice, endTime }, event, "live", { applyPatch: true, queueHydration: true });
    };

    const onNewHighestBid = (...params) => {
      const event = params[params.length - 1];
      const [tokenId, bidder, amount] = params;
      traceMarketplaceEvent("live event", {
        eventName: "NewHighestBid",
        tokenId: normalizeTokenId(tokenId),
        blockNumber: Number(event?.blockNumber || 0),
        txHash: event?.transactionHash || "",
      });
      indexEvent("NewHighestBid", { tokenId, bidder, amount }, event, "live", { applyPatch: true, queueHydration: true });
    };

    const onAuctionEnded = (...params) => {
      const event = params[params.length - 1];
      const [tokenId, winner, amount] = params;
      traceMarketplaceEvent("live event", {
        eventName: "AuctionEnded",
        tokenId: normalizeTokenId(tokenId),
        blockNumber: Number(event?.blockNumber || 0),
        txHash: event?.transactionHash || "",
      });
      indexEvent("AuctionEnded", { tokenId, winner, amount }, event, "live", { applyPatch: true, queueHydration: true });
    };

    traceMarketplaceEvent("live listeners attached");

    nft.on("Transfer", onTransfer);
    nft.on("CarbonCreditRetired", onRetired);
    marketplace.on("TokenListed", onTokenListed);
    marketplace.on("ListingCancelled", onListingCancelled);
    marketplace.on("TokenPurchased", onTokenPurchased);
    marketplace.on("AuctionStarted", onAuctionStarted);
    marketplace.on("NewHighestBid", onNewHighestBid);
    marketplace.on("AuctionEnded", onAuctionEnded);

    if (replayTimerRef.current) {
      window.clearInterval(replayTimerRef.current);
    }

    replayTimerRef.current = window.setInterval(async () => {
      if (!isSessionActive(sessionId)) return;
      await replayEventsInRange({
        trigger: "poll",
        sessionId,
      });
    }, clampToPositiveInt(process.env.REACT_APP_MARKETPLACE_REPLAY_POLL_MS, 9000));

    const onWindowFocus = async () => {
      if (!isSessionActive(sessionId)) return;
      await replayEventsInRange({
        trigger: "focus",
        sessionId,
      });
    };

    const onVisibility = () => {
      if (!document.hidden) {
        onWindowFocus();
      }
    };

    window.addEventListener("focus", onWindowFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      traceMarketplaceEvent("live listeners detached");

      nft.off("Transfer", onTransfer);
      nft.off("CarbonCreditRetired", onRetired);
      marketplace.off("TokenListed", onTokenListed);
      marketplace.off("ListingCancelled", onListingCancelled);
      marketplace.off("TokenPurchased", onTokenPurchased);
      marketplace.off("AuctionStarted", onAuctionStarted);
      marketplace.off("NewHighestBid", onNewHighestBid);
      marketplace.off("AuctionEnded", onAuctionEnded);

      window.removeEventListener("focus", onWindowFocus);
      document.removeEventListener("visibilitychange", onVisibility);

      if (replayTimerRef.current) {
        window.clearInterval(replayTimerRef.current);
        replayTimerRef.current = null;
      }
    };
  }, [indexEvent, isSessionActive, replayEventsInRange]);

  useEffect(() => {
    if (activeAuctionTokenIds.length === 0) return undefined;

    const intervalId = window.setInterval(() => {
      setNow(Math.floor(Date.now() / 1000));
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [activeAuctionTokenIds]);

  useEffect(() => {
    if (!cacheKey) return;
    if (persistTimerRef.current) {
      window.clearTimeout(persistTimerRef.current);
    }

    persistTimerRef.current = window.setTimeout(() => {
      const snapshot = {
        schemaVersion: MARKETPLACE_CACHE_SCHEMA_VERSION,
        state: {
          tokensById: state.tokensById,
          tokenOrder: state.tokenOrder,
          metadataByUri: state.metadataByUri,
          eventsByTokenId: state.eventsByTokenId,
          bidHistoryByKey: state.bidHistoryByKey,
          blockTimestampByNumber: state.blockTimestampByNumber,
          lastScannedBlocks: state.lastScannedBlocks,
          eventCursor: state.eventCursor,
          loadingStatus: {
            staleFromCache: false,
            fromCache: state.loadingStatus.fromCache,
          },
        },
        savedAt: Date.now(),
      };

      try {
        window.localStorage.setItem(cacheKey, JSON.stringify(snapshot, cacheJsonReplacer));
      } catch (error) {
        if (DEV_MODE) {
          console.warn("[marketplace] failed to persist cache", error);
        }
      }
    }, 500);

    return () => {
      if (persistTimerRef.current) {
        window.clearTimeout(persistTimerRef.current);
      }
    };
  }, [
    cacheKey,
    state.tokensById,
    state.tokenOrder,
    state.metadataByUri,
    state.eventsByTokenId,
    state.bidHistoryByKey,
    state.blockTimestampByNumber,
    state.lastScannedBlocks,
    state.eventCursor,
    state.loadingStatus.fromCache,
  ]);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      cancelAllInFlightWork();

      if (replayTimerRef.current) window.clearInterval(replayTimerRef.current);
      if (persistTimerRef.current) window.clearTimeout(persistTimerRef.current);
    };
  }, [cancelAllInFlightWork]);

  return {
    chainId,
    currentAccount,
    now,
    marketplaceReadContract: contractsRef.current.marketplace,
    nftReadContract: contractsRef.current.nft,

    state,
    tokensById: state.tokensById,
    tokenOrder: state.tokenOrder,
    marketplaceNfts: tokensArray,
    eventsByTokenId: state.eventsByTokenId,

    filteredTokenIds,
    paginatedTokenIds,
    paginatedNfts,

    counts: {
      all: tokensArray.length,
      listed: listedCount,
      auction: auctionCount,
      retired: retiredCount,
    },

    loadingStatus: state.loadingStatus,
    errors: state.errors,

    hydrateDynamicToken,
    hydrateFullToken,
    hydrateVisiblePage,
    recomputeOwnershipForAccount,
    optimisticPatchToken,
    processReceiptLogs,
    queueDirtyToken,
    fetchBidHistoryForToken,
    fetchAuditHistoryForToken,
  };
}
